import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  Ambulance,
  Bell,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleHelp,
  ClipboardCheck,
  Clock3,
  Download,
  FileCheck2,
  FileText,
  Filter,
  HeartPulse,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  Menu,
  MoreHorizontal,
  MoreVertical,
  PackageSearch,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Stethoscope,
  UserCog,
  Users,
  X,
  Zap,
} from "lucide-react";

const navGroups = [
  {
    label: "PILOTAGE",
    items: [
      ["dashboard", "Vue d’ensemble", LayoutDashboard],
      ["monitoring", "Monitoring en direct", Activity],
      ["appointments", "Rendez-vous", CalendarDays],
      ["agenda", "Agenda", Clock3],
    ],
  },
  {
    label: "ÉQUIPES & ACCÈS",
    items: [
      ["users", "Utilisateurs", Users],
      ["practitioners", "Praticiens", Stethoscope],
      ["roles", "Rôles & privilèges", ShieldCheck],
      ["validations", "Validations", ClipboardCheck],
    ],
  },
  {
    label: "RÉSEAU DE SOINS",
    items: [
      ["facilities", "Établissements", Building2],
      ["pharmacies", "Pharmacies", PackageSearch],
      ["ambulances", "Ambulances", Ambulance],
    ],
  },
  {
    label: "GOUVERNANCE",
    items: [
      ["catalog", "Référentiels", FileText],
      ["notifications", "Communications", Bell],
      ["audit", "Journal d’audit", FileCheck2],
      ["settings", "Paramètres", Settings],
    ],
  },
];

const pageMeta = {
  dashboard: [
    "Vue d’ensemble",
    "Suivez l’activité et les priorités de votre établissement.",
  ],
  monitoring: [
    "Monitoring en direct",
    "Surveillez les files d’attente, les appels et les utilisateurs connectés.",
  ],
  appointments: [
    "Rendez-vous",
    "Pilotez les demandes, confirmations et consultations.",
  ],
  agenda: [
    "Agenda de l’établissement",
    "Visualisez les équipes et organisez les créneaux.",
  ],
  users: [
    "Utilisateurs",
    "Gérez les membres, leurs rôles et leur périmètre d’accès.",
  ],
  practitioners: [
    "Praticiens",
    "Gérez les professionnels rattachés à vos structures.",
  ],
  roles: [
    "Rôles & privilèges",
    "Définissez précisément les responsabilités de chaque équipe.",
  ],
  validations: [
    "Validation des professionnels",
    "Contrôlez les justificatifs avant activation.",
  ],
  facilities: [
    "Établissements",
    "Administrez les structures et leurs responsables.",
  ],
  pharmacies: ["Pharmacies", "Gérez les officines, horaires et gardes."],
  ambulances: [
    "Services d’ambulance",
    "Supervisez les services, véhicules et missions.",
  ],
  catalog: ["Référentiels", "Centralisez spécialités, documents et motifs."],
  notifications: [
    "Communications",
    "Diffusez des annonces et gérez les modèles.",
  ],
  audit: [
    "Journal d’audit",
    "Retrouvez chaque action sensible de façon traçable.",
  ],
  settings: [
    "Paramètres",
    "Configurez la sécurité et les règles de la plateforme.",
  ],
};

const MEDICAL_SERVICE_GROUPS = {
  "Soins généraux et prévention": [
    "Médecine générale", "Médecine familiale", "Médecine interne",
    "Médecine préventive", "Gériatrie", "Soins infirmiers", "Vaccination",
    "Nutrition et diététique", "Médecine du travail", "Médecine du sport",
    "Consultation de voyage", "Soins palliatifs", "Hospitalisation à domicile",
  ],
  "Femme, mère et enfant": [
    "Gynécologie", "Obstétrique", "Gynécologie-obstétrique", "Maternité",
    "Planification familiale", "Fertilité et procréation médicalement assistée",
    "Sénologie", "Pédiatrie", "Néonatologie", "Chirurgie pédiatrique",
  ],
  "Spécialités médicales": [
    "Allergologie", "Cardiologie", "Dermatologie", "Endocrinologie-diabétologie",
    "Gastro-entérologie et hépatologie", "Hématologie", "Infectiologie",
    "Médecine tropicale", "Néphrologie", "Neurologie", "Oncologie médicale",
    "Pneumologie", "Rhumatologie", "Immunologie clinique", "Génétique médicale",
  ],
  "Spécialités chirurgicales": [
    "Anesthésie-réanimation", "Chirurgie générale", "Chirurgie digestive",
    "Chirurgie cardiovasculaire", "Chirurgie thoracique", "Chirurgie plastique et reconstructrice",
    "Chirurgie maxillo-faciale", "Neurochirurgie", "Orthopédie-traumatologie",
    "Urologie", "Chirurgie vasculaire", "Chirurgie oncologique", "Transplantation",
  ],
  "Organes et appareils": [
    "Dentisterie", "Orthodontie", "Parodontologie", "Stomatologie",
    "Ophtalmologie", "ORL", "Audiologie", "Phoniatrie",
  ],
  "Santé mentale et accompagnement": [
    "Psychiatrie", "Pédopsychiatrie", "Psychologie clinique", "Psychothérapie",
    "Addictologie", "Sexologie", "Assistance sociale",
  ],
  "Diagnostic et explorations": [
    "Analyses médicales", "Anatomopathologie", "Biologie médicale",
    "Imagerie médicale", "Radiologie", "Échographie", "Mammographie",
    "Scanner", "IRM", "Médecine nucléaire", "Endoscopie", "Explorations fonctionnelles",
  ],
  "Rééducation et soins complémentaires": [
    "Kinésithérapie", "Physiothérapie", "Ergothérapie", "Orthophonie",
    "Psychomotricité", "Médecine physique et réadaptation", "Podologie",
    "Prothèses et orthèses", "Ostéopathie", "Rééducation cardiaque",
  ],
  "Urgences et soins critiques": [
    "Urgences", "Réanimation", "Soins intensifs", "SAMU et transport médicalisé",
    "Traumatologie", "Centre antipoison", "Centre de traitement des brûlés",
  ],
  "Services médico-techniques": [
    "Pharmacie", "Pharmacie clinique", "Transfusion sanguine", "Banque de sang",
    "Dialyse", "Chimiothérapie", "Radiothérapie", "Stérilisation médicale",
    "Médecine légale", "Téléconsultation",
  ],
};

function ServiceSelector({ initial = [] }) {
  const serviceKey = (value) => String(value || "").trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const canonicalServices = Object.values(MEDICAL_SERVICE_GROUPS).flat();
  const canonicalByKey = new Map(canonicalServices.map((service) => [serviceKey(service), service]));
  const normalizedInitial = Array.from(new Map(initial.map((service) => {
    const canonical = canonicalByKey.get(serviceKey(service)) || String(service).trim();
    return [serviceKey(canonical), canonical];
  })).values()).filter(Boolean);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set(normalizedInitial));
  const normalized = query.trim().toLowerCase();
  const known = new Set(Object.values(MEDICAL_SERVICE_GROUPS).flat());
  const groups = { ...MEDICAL_SERVICE_GROUPS };
  const legacy = normalizedInitial.filter((service) => !known.has(service));
  if (legacy.length) groups["Services déjà enregistrés"] = legacy;
  return (
    <div className="service-selector-wrap">
      {[...selected].map((service) => <input key={service} type="hidden" name="services" value={service} />)}
      <div className="service-selector-head">
        <strong>{selected.size} service{selected.size > 1 ? "s" : ""} attribué{selected.size > 1 ? "s" : ""}</strong>
        <div>
          <button type="button" onClick={() => setSelected(new Set(known))}>Tout sélectionner</button>
          <button type="button" onClick={() => setSelected(new Set())}>Tout retirer</button>
        </div>
      </div>
      <label className="service-filter"><Search size={15} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un service médical…" /></label>
      <div className="service-selector">
        {Object.entries(groups).map(([group, services]) => {
          const visible = services.filter((service) => service.toLowerCase().includes(normalized));
          return visible.length ? <section key={group} className="service-group">
            <strong>{group}</strong>
            <div>{visible.map((service) => (
              <label key={service}>
                <input type="checkbox" checked={selected.has(service)} onChange={(event) => setSelected((current) => {
                  const next = new Set(current);
                  event.target.checked ? next.add(service) : next.delete(service);
                  return next;
                })} />
                <span>{service}</span>
              </label>
            ))}</div>
          </section> : null;
        })}
      </div>
    </div>
  );
}

const users = [
  {
    name: "Dr Sarah Mbala",
    initials: "SM",
    contact: "sarah.mbala@elikyacare.com",
    phone: "+243 812 450 901",
    role: "Médecin",
    scope: "Centre Médical Ngaliema",
    status: "Actif",
    seen: "Aujourd’hui, 08:42",
  },
  {
    name: "Grâce Ilunga",
    initials: "GI",
    contact: "grace.ilunga@elikyacare.com",
    phone: "+243 998 041 226",
    role: "Gestionnaire",
    scope: "Centre Médical Ngaliema",
    status: "Actif",
    seen: "Hier, 17:26",
  },
  {
    name: "Patrick Lufungula",
    initials: "PL",
    contact: "patrick.l@elikyacare.com",
    phone: "+243 822 136 784",
    role: "Secrétaire",
    scope: "Clinique Bondeko",
    status: "Invité",
    seen: "Invitation envoyée",
  },
  {
    name: "Dr Jonathan Kanku",
    initials: "JK",
    contact: "jonathan.kanku@elikyacare.com",
    phone: "+243 974 220 518",
    role: "Médecin",
    scope: "Hôpital du Cinquantenaire",
    status: "Actif",
    seen: "19 août, 15:11",
  },
  {
    name: "Chantal Beya",
    initials: "CB",
    contact: "chantal.beya@elikyacare.com",
    phone: "+243 810 774 390",
    role: "Auditeur",
    scope: "Toutes les structures",
    status: "Désactivé",
    seen: "12 août, 09:30",
  },
];

const appointments = [
  {
    time: "08:30",
    patient: "Jean Kalala",
    doctor: "Dr Sarah Mbala",
    reason: "Consultation générale",
    type: "Cabinet",
    status: "Confirmé",
  },
  {
    time: "09:15",
    patient: "Mireille Tshibanda",
    doctor: "Dr Sarah Mbala",
    reason: "Suivi cardiologique",
    type: "Téléconsultation",
    status: "En attente",
  },
  {
    time: "10:00",
    patient: "David Mbuyi",
    doctor: "Dr Jonathan Kanku",
    reason: "Contrôle post-opératoire",
    type: "Cabinet",
    status: "Confirmé",
  },
  {
    time: "11:30",
    patient: "Clarisse Mutombo",
    doctor: "Dr Sarah Mbala",
    reason: "Première consultation",
    type: "Cabinet",
    status: "En attente",
  },
  {
    time: "14:00",
    patient: "Alain Kabeya",
    doctor: "Dr Jonathan Kanku",
    reason: "Résultats d’analyse",
    type: "Téléconsultation",
    status: "Terminé",
  },
];

const moduleRows = {
  practitioners: [
    ["Dr Sarah Mbala", "Cardiologie", "Centre Médical Ngaliema", "Actif"],
    [
      "Dr Jonathan Kanku",
      "Médecine générale",
      "Hôpital du Cinquantenaire",
      "Actif",
    ],
    ["Dr Nadège Bolamba", "Pédiatrie", "Clinique Bondeko", "En attente"],
    ["Dr Samuel Ekanga", "Radiologie", "Centre Médical Ngaliema", "Suspendu"],
  ],
  validations: [
    [
      "Dr Nadège Bolamba",
      "Médecin · Pédiatrie",
      "Clinique Bondeko",
      "3 pièces",
      "En vérification",
    ],
    ["Jean Moke", "Ambulancier", "Secours Kinshasa", "4 pièces", "En attente"],
    [
      "Dr Léon Kabamba",
      "Médecin · ORL",
      "Hôpital du Cinquantenaire",
      "2 pièces",
      "Document manquant",
    ],
  ],
  facilities: [
    [
      "Centre Médical Ngaliema",
      "Clinique",
      "Kinshasa · Gombe",
      "28 membres",
      "Actif",
    ],
    [
      "Hôpital du Cinquantenaire",
      "Hôpital",
      "Kinshasa · Kasa-Vubu",
      "64 membres",
      "Actif",
    ],
    [
      "Clinique Bondeko",
      "Clinique",
      "Kinshasa · Limete",
      "17 membres",
      "Actif",
    ],
    [
      "Laboratoire Biocare",
      "Laboratoire",
      "Kinshasa · Ngaliema",
      "9 membres",
      "En configuration",
    ],
  ],
  pharmacies: [
    [
      "Pharmacie Espérance",
      "Kinshasa · Gombe",
      "07:00 – 22:00",
      "De garde",
      "Actif",
    ],
    [
      "Pharmacie du Fleuve",
      "Kinshasa · Ngaliema",
      "08:00 – 20:00",
      "Standard",
      "Actif",
    ],
    [
      "Pharmacie Santé Plus",
      "Kinshasa · Limete",
      "24 h / 24",
      "De garde",
      "Actif",
    ],
  ],
  ambulances: [
    [
      "Secours Kinshasa",
      "Gombe · Ngaliema",
      "6 véhicules",
      "2 missions",
      "Actif",
    ],
    [
      "Urgences Médicales 24",
      "Kinshasa Centre",
      "4 véhicules",
      "1 mission",
      "Actif",
    ],
    [
      "Ambulance Bondeko",
      "Limete · Lemba",
      "2 véhicules",
      "Disponible",
      "Maintenance",
    ],
  ],
  catalog: [
    ["Cardiologie", "Spécialité médicale", "24 praticiens", "Actif"],
    ["Compte-rendu opératoire", "Type de document", "12 usages", "Actif"],
    ["Indisponibilité du médecin", "Motif d’annulation", "38 usages", "Actif"],
    ["Douleur thoracique", "Motif d’urgence", "16 usages", "Actif"],
  ],
  notifications: [
    [
      "Confirmation de rendez-vous",
      "Push + SMS",
      "Patients",
      "Automatique",
      "Actif",
    ],
    ["Rappel J-1", "Push", "Patients", "Automatique", "Actif"],
    [
      "Maintenance planifiée",
      "Bannière",
      "Tous les utilisateurs",
      "Brouillon",
      "Brouillon",
    ],
  ],
  audit: [
    [
      "20/08/2026 · 08:42:11",
      "Grâce Ilunga",
      "USER.PERMISSION_UPDATE",
      "Patrick Lufungula",
      "Réussi",
    ],
    [
      "20/08/2026 · 08:31:04",
      "Dr Sarah Mbala",
      "APPOINTMENT.CONFIRM",
      "RDV #KMC-2094",
      "Réussi",
    ],
    [
      "20/08/2026 · 07:58:22",
      "Admin plateforme",
      "PRACTITIONER.APPROVE",
      "Dr Jonathan Kanku",
      "Réussi",
    ],
    [
      "19/08/2026 · 18:14:39",
      "Système",
      "SESSION.REVOKE",
      "Utilisateur #1048",
      "Réussi",
    ],
  ],
};

function Logo() {
  return (
    <div className="brand">
      <div className="brand-mark">
        <HeartPulse size={23} />
      </div>
      <div>
        <strong>ElikyaCare</strong>
        <span>ADMINISTRATION</span>
      </div>
    </div>
  );
}
function Badge({ children }) {
  const key = String(children).toLowerCase().replace("é", "e");
  return (
    <span className={`badge ${key.replaceAll(" ", "-")}`}>{children}</span>
  );
}
function IconButton({ children, label, onClick }) {
  return (
    <button
      type="button"
      className="icon-btn"
      aria-label={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function Sidebar({ page, setPage, open, setOpen, mode, collapsed, setCollapsed }) {
  const allowed =
    mode === "PLATFORM_ADMIN"
      ? new Set([
          "dashboard",
          "monitoring",
          "users",
          "validations",
          "practitioners",
          "facilities",
          "ambulances",
          "audit",
        ])
      : new Set([
          "dashboard",
          "monitoring",
          "appointments",
          "agenda",
          "users",
          "practitioners",
          "roles",
          "audit",
        ]);
  return (
    <aside className={`sidebar ${open ? "open" : ""} ${collapsed ? "collapsed" : ""}`}>
      <div className="sidebar-head">
        <Logo />
        <span className="desktop-sidebar-toggle">
          <IconButton label={collapsed ? "Déplier le menu" : "Plier le menu"} onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}
          </IconButton>
        </span>
        <span className="mobile-sidebar-close">
          <IconButton label="Fermer le menu" onClick={() => setOpen(false)}><X size={19} /></IconButton>
        </span>
      </div>
      <nav>
        {navGroups.map((g) => {
          const items = g.items.filter(([id]) => allowed.has(id));
          return items.length ? (
            <div className="nav-group" key={g.label}>
              <small>{g.label}</small>
              {items.map(([id, label, Icon]) => (
                <button
                  key={id}
                  className={page === id ? "active" : ""}
                  onClick={() => {
                    setPage(id);
                    setOpen(false);
                  }}
                >
                  <Icon size={18} />
                  <span>
                    {mode === "PLATFORM_ADMIN" && id === "practitioners"
                      ? "Prestataires"
                      : label}
                  </span>
                </button>
              ))}
            </div>
          ) : null;
        })}
      </nav>
      <div className="sidebar-foot">
        <div className="security">
          <ShieldCheck size={18} />
          <div>
            <strong>Environnement sécurisé</strong>
            <span>Dernière activité : 08:42</span>
          </div>
        </div>
        <button>
          <CircleHelp size={18} />
          Centre d’aide
        </button>
      </div>
    </aside>
  );
}

function Header({ setOpen, overview, onLogout, onFacilityChange, query, setQuery }) {
  const admin = overview.mode === "PLATFORM_ADMIN";
  const name = overview.identity?.name || "Utilisateur";
  return (
    <header className="topbar">
      <IconButton label="Ouvrir le menu" onClick={() => setOpen(true)}>
        <Menu size={21} />
      </IconButton>
      <div className="context">
        <span>{admin ? "PÉRIMÈTRE" : "ÉTABLISSEMENT RATTACHÉ"}</span>
        {admin ? (
          <button>Validation plateforme</button>
        ) : (overview.available_facilities || []).length > 1 ? (
          <select
            className="facility-switcher"
            value={overview.facility?.id || ""}
            onChange={(e) => onFacilityChange(e.target.value)}
          >
            {overview.available_facilities.map((facility) => (
              <option value={facility.id} key={facility.id}>
                {facility.name} · {facility.role}
              </option>
            ))}
          </select>
        ) : (
          <button>{overview.facility?.name}</button>
        )}
      </div>
      <div className="top-actions">
        <label className="global-search">
          <Search size={17} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filtrer ce tableau…"
            aria-label="Filtrer le tableau en cours"
          />
          <kbd>⌘ K</kbd>
        </label>
        <IconButton label="Notifications">
          <Bell size={19} />
        </IconButton>
        <div className="user-menu">
          <span>
            {name
              .split(" ")
              .map((x) => x[0])
              .join("")
              .slice(0, 2)}
          </span>
          <div>
            <strong>{name}</strong>
            <small>{overview.identity?.role}</small>
          </div>
          <ChevronDown size={15} />
        </div>
        <button
          className="logout-button"
          type="button"
          onClick={onLogout}
          aria-label="Se déconnecter du backoffice"
          title="Déconnexion"
        >
          <LogOut size={17} />
          <span>Déconnexion</span>
        </button>
      </div>
    </header>
  );
}

function GlobalSearchDialog({ overview, setPage, onClose }) {
  const [query, setQuery] = useState("");
  const normalized = query.trim().toLowerCase();
  const sources = [
    ["facilities", "Établissement", overview.facilities || []],
    ["users", "Utilisateur", overview.users || overview.members || []],
    ["practitioners", "Prestataire", overview.providers || overview.practitioners || []],
    ["appointments", "Rendez-vous", overview.appointments || []],
  ];
  const results = normalized
    ? sources.flatMap(([page, type, rows]) => rows
        .filter((row) => JSON.stringify(row).toLowerCase().includes(normalized))
        .slice(0, 6)
        .map((row) => ({ page, type, row })))
    : [];
  const label = (result) => result.row.name || result.row.patient || result.row.doctor || result.row.address || `Élément ${result.row.id}`;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="global-search-dialog" role="dialog" aria-label="Recherche globale">
        <div className="global-search-input">
          <Search size={20} />
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un établissement, utilisateur, praticien ou rendez-vous…" />
          <IconButton label="Fermer" onClick={onClose}><X /></IconButton>
        </div>
        <div className="global-search-results">
          {!normalized && <p>Saisissez au moins un caractère pour lancer la recherche.</p>}
          {normalized && !results.length && <p>Aucun résultat trouvé.</p>}
          {results.map((result, index) => (
            <button key={`${result.page}-${result.row.id || index}`} onClick={() => { setPage(result.page); onClose(); }}>
              <Search size={16} />
              <span><strong>{label(result)}</strong><small>{result.type}</small></span>
              <ChevronRight size={16} />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function PageHeader({ page, onAdd }) {
  const [title, desc] = pageMeta[page];
  const labels = {
    users: "Nouvel utilisateur",
    appointments: "Nouveau rendez-vous",
    agenda: "Nouveau créneau",
    practitioners: "Ajouter un praticien",
    facilities: "Nouvel établissement",
    pharmacies: "Nouvelle pharmacie",
    ambulances: "Nouveau service",
    roles: "Nouveau rôle",
    notifications: "Nouvelle communication",
    catalog: "Nouvel élément",
  };
  return (
    <div className="page-header">
      <div>
        <div className="breadcrumb">
          Administration <ChevronRight size={13} /> {title}
        </div>
        <h1>{title}</h1>
        <p>{desc}</p>
      </div>
      {labels[page] && onAdd && (
        <button className="primary" onClick={onAdd}>
          <Plus size={18} />
          {labels[page]}
        </button>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, trend, tone = "blue" }) {
  return (
    <article className={`stat ${tone}`}>
      <div className="stat-icon">
        <Icon size={20} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{trend}</small>
      </div>
    </article>
  );
}
function MiniChart() {
  return (
    <div className="chart">
      <div className="chart-labels">
        <span>60</span>
        <span>40</span>
        <span>20</span>
        <span>0</span>
      </div>
      <svg
        viewBox="0 0 700 190"
        preserveAspectRatio="none"
        aria-label="Évolution des rendez-vous"
      >
        <defs>
          <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#1570d8" stopOpacity=".2" />
            <stop offset="1" stopColor="#1570d8" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0 158 C70 148 90 124 145 131 S235 91 290 102 S370 62 430 76 S530 42 580 58 S650 27 700 35 L700 190 L0 190Z"
          fill="url(#area)"
        />
        <path
          d="M0 158 C70 148 90 124 145 131 S235 91 290 102 S370 62 430 76 S530 42 580 58 S650 27 700 35"
          fill="none"
          stroke="#1264be"
          strokeWidth="3"
        />
        <g fill="#fff" stroke="#1264be" strokeWidth="3">
          {[
            [0, 158],
            [145, 131],
            [290, 102],
            [430, 76],
            [580, 58],
            [700, 35],
          ].map((p, i) => (
            <circle key={i} cx={p[0]} cy={p[1]} r="4" />
          ))}
        </g>
      </svg>
      <div className="chart-days">
        <span>Lun</span>
        <span>Mar</span>
        <span>Mer</span>
        <span>Jeu</span>
        <span>Ven</span>
        <span>Sam</span>
        <span>Dim</span>
      </div>
    </div>
  );
}

function DoctorAppointmentStats({ rows = [] }) {
  const maximum = Math.max(...rows.map((row) => row.total), 1);
  return (
    <section className="card analytics-card">
      <div className="card-head">
        <div>
          <h2>Rendez-vous par médecin</h2>
          <p>Répartition réelle de l’activité de l’établissement</p>
        </div>
        <span className="data-badge">PostgreSQL · temps réel</span>
      </div>
      <div className="doctor-stats">
        {rows.map((row) => (
          <article key={row.doctor}>
            <div className="doctor-stat-head">
              <span className="doctor-avatar">
                {row.doctor
                  .split(" ")
                  .map((x) => x[0])
                  .join("")
                  .slice(0, 2)}
              </span>
              <div>
                <strong>{row.doctor}</strong>
                <small>{row.total} rendez-vous</small>
              </div>
              <b>{row.total}</b>
            </div>
            <div className="metric-bar">
              <i style={{ width: `${(row.total / maximum) * 100}%` }} />
            </div>
            <div className="status-metrics">
              <span>
                <i className="pending" />
                {row.pending} en attente
              </span>
              <span>
                <i className="confirmed" />
                {row.confirmed} confirmés
              </span>
              <span>
                <i className="completed" />
                {row.completed} terminés
              </span>
              <span>
                <i className="cancelled" />
                {row.cancelled} annulés/refusés
              </span>
            </div>
          </article>
        ))}
        {!rows.length && (
          <div className="empty-state">
            Aucun rendez-vous enregistré pour les médecins de cet établissement.
          </div>
        )}
      </div>
    </section>
  );
}

function SubscriptionStats({ overview }) {
  const stats = overview.subscription_statistics || {};
  const profiles = stats.by_profile || [];
  const maximum = Math.max(...profiles.map((row) => row.total), 1);
  return (
    <section className="card analytics-card subscriptions-card">
      <div className="card-head">
        <div>
          <h2>Souscriptions à la plateforme</h2>
          <p>Comptes patients et prestataires inscrits dans ElikyaCare</p>
        </div>
        <span className="data-badge">Inscriptions réelles</span>
      </div>
      <div className="subscription-layout">
        <div className="subscription-bars">
          {profiles.map((row) => (
            <div key={row.profile}>
              <header>
                <span>{row.profile}</span>
                <strong>{row.total}</strong>
              </header>
              <div className="metric-bar">
                <i style={{ width: `${(row.total / maximum) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
        <div className="monthly-subscriptions">
          <h3>Évolution mensuelle</h3>
          {(stats.monthly || []).map((row) => (
            <div key={row.month}>
              <span>
                {new Date(`${row.month}-01`).toLocaleDateString("fr-FR", {
                  month: "short",
                  year: "numeric",
                })}
              </span>
              <strong>{row.total}</strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FacilityCallControls({ call, onRefresh }) {
  const [state, setState] = useState(call.status);
  const [error, setError] = useState("");
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const remoteCandidates = useRef(new Set());
  const sessionRef = useRef(
    localStorage.getItem("elikyacare_monitoring_audio_session") || crypto.randomUUID(),
  );
  localStorage.setItem("elikyacare_monitoring_audio_session", sessionRef.current);

  const request = async (suffix, options = {}) => {
    const token = sessionStorage.getItem("kimyacare_access");
    const response = await fetch(
      `${API_BASE}/platform/facility/voice-calls/${call.id}/${suffix}`,
      {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      },
    );
    const body = await response.json();
    if (!response.ok) throw new Error(body.detail || "Action impossible.");
    return body;
  };

  const stopMedia = () => {
    window.clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    peerRef.current?.close();
    streamRef.current = null;
    peerRef.current = null;
  };

  useEffect(() => () => stopMedia(), []);

  const decline = async () => {
    try {
      await request("action/", {
        method: "PUT",
        body: JSON.stringify({ action: "decline", session_id: sessionRef.current }),
      });
      onRefresh();
    } catch (reason) {
      setError(reason.message);
    }
  };

  const answer = async () => {
    try {
      setError("");
      setState("CONNECTING");
      await request("action/", {
        method: "PUT",
        body: JSON.stringify({ action: "claim", session_id: sessionRef.current }),
      });
      const initial = await request("signal/");
      const local = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      streamRef.current = local;
      const peer = new RTCPeerConnection({ iceServers: initial.ice_servers || [] });
      peerRef.current = peer;
      local.getAudioTracks().forEach((track) => peer.addTrack(track, local));
      peer.ontrack = (event) => {
        if (audioRef.current && event.streams[0]) {
          audioRef.current.srcObject = event.streams[0];
          audioRef.current.play().catch(() => {});
        }
      };
      peer.onicecandidate = (event) => {
        if (!event.candidate) return;
        request("signal/", {
          method: "POST",
          body: JSON.stringify({
            action: "candidate", session_id: sessionRef.current,
            candidate: event.candidate.toJSON(),
          }),
        }).catch(() => {});
      };

      let answered = false;
      const synchronize = async () => {
        const signal = await request("signal/");
        setState(signal.status);
        if (!answered && signal.offer?.sdp) {
          await peer.setRemoteDescription(signal.offer);
          const response = await peer.createAnswer();
          await peer.setLocalDescription(response);
          await request("signal/", {
            method: "POST",
            body: JSON.stringify({
              action: "answer", session_id: sessionRef.current,
              description: response,
            }),
          });
          answered = true;
          setState("ACTIVE");
        }
        for (const candidate of signal.remote_candidates || []) {
          const key = candidate.candidate || "";
          if (!key || remoteCandidates.current.has(key)) continue;
          remoteCandidates.current.add(key);
          await peer.addIceCandidate(candidate);
        }
        if (["ENDED", "DECLINED", "MISSED"].includes(signal.status)) {
          stopMedia();
          onRefresh();
        }
      };
      await synchronize();
      timerRef.current = window.setInterval(() => synchronize().catch(() => {}), 800);
    } catch (reason) {
      stopMedia();
      setState(call.status);
      setError(reason.message);
    }
  };

  const end = async () => {
    try {
      await request("action/", {
        method: "PUT",
        body: JSON.stringify({ action: "end", session_id: sessionRef.current }),
      });
    } finally {
      stopMedia();
      onRefresh();
    }
  };

  return (
    <div className="monitoring-call-actions">
      <audio ref={audioRef} autoPlay />
      {state === "RINGING" ? (
        <><button className="primary compact" onClick={answer}>Répondre</button><button className="ghost compact" onClick={decline}>Refuser</button></>
      ) : ["CONNECTING", "ACTIVE", "HELD"].includes(state) ? (
        <button className="danger compact" onClick={end}>Terminer</button>
      ) : null}
      {error && <small className="call-error">{error}</small>}
    </div>
  );
}

function MonitoringPage({ overview, onRefresh }) {
  const data = overview.monitoring || {};
  const queues = [
    ["Appels entrants du centre", data.facility_calls || [], "caller", "facility"],
    ["Appels ambulance", data.voice_calls || [], "caller", "ambulance"],
    ["Téléconsultations", data.consultations || [], "patient", "doctor"],
    ["Demandes d’ambulance", data.ambulance_requests || [], "patient", "provider"],
  ];
  return (
    <div className="page-stack">
      <section className="stats-grid">
        <Stat icon={Activity} label="Appels actifs" value={(data.facility_calls || []).length + (data.voice_calls || []).length} trend="Centre et ambulance" />
        <Stat icon={Clock3} label="En attente" value={(data.consultations || []).filter((x) => x.status === "WAITING").length} trend="Téléconsultations" tone="amber" />
        <Stat icon={Ambulance} label="Missions ouvertes" value={(data.ambulance_requests || []).length} trend="Demandes en traitement" tone="teal" />
        <Stat icon={Users} label="Utilisateurs en ligne" value={(data.online_users || []).length} trend="5 dernières minutes" tone="violet" />
      </section>
      {queues.map(([title, rows, primary, secondary], queueIndex) => (
        <section className="panel" key={title}>
          <div className="panel-head"><div><h3>{title}</h3><p>Mise à jour avec les données temps réel du serveur</p></div><Badge>{rows.length}</Badge></div>
          <div className="table-wrap"><table><thead><tr><th>Interlocuteur</th><th>Destination</th><th>État</th><th>Temps</th>{queueIndex === 0 && <th>Prise d’appel</th>}</tr></thead>
            <tbody>{rows.map((row) => <tr key={row.id}><td><strong>{row[primary]}</strong></td><td>{row[secondary]}</td><td><Badge>{row.status}</Badge></td><td>{Math.max(0, row.duration_seconds ?? row.waiting_seconds ?? 0)} s</td>{queueIndex === 0 && <td><FacilityCallControls call={row} onRefresh={onRefresh} /></td>}</tr>)}</tbody>
          </table></div>
          {!rows.length && <p className="empty-state">Aucune activité dans cette file.</p>}
        </section>
      ))}
      <section className="panel"><div className="panel-head"><div><h3>Présence en ligne</h3><p>Activité détectée durant les cinq dernières minutes</p></div></div>
        <div className="table-wrap"><table><thead><tr><th>Utilisateur</th><th>Profil</th><th>Dernière activité</th></tr></thead><tbody>
          {(data.online_users || []).map((row) => <tr key={row.id}><td><strong>{row.name}</strong></td><td>{row.role}</td><td>{formatDate(row.last_seen_at)}</td></tr>)}
        </tbody></table></div>
      </section>
    </div>
  );
}

function Dashboard({ setPage, overview }) {
  const s = overview.summary || {};
  const admin = overview.mode === "PLATFORM_ADMIN";
  return (
    <>
      <div className="stats-grid">
        {admin ? (
          <>
            <Stat
              icon={ClipboardCheck}
              label="Validations en attente"
              value={s.pending_validations || 0}
              trend="Données PostgreSQL"
              tone="amber"
            />
            <Stat
              icon={Building2}
              label="Établissements"
              value={s.facilities || 0}
              trend={`${s.active_facilities || 0} actifs`}
            />
            <Stat
              icon={Users}
              label="Comptes plateforme"
              value={s.users || 0}
              trend="Tous profils confondus"
              tone="teal"
            />
            <Stat
              icon={HeartPulse}
              label="Souscriptions"
              value={s.subscriptions || 0}
              trend={`${s.new_subscriptions_month || 0} nouvelles ce mois`}
              tone="violet"
            />
          </>
        ) : (
          <>
            <Stat
              icon={CalendarDays}
              label="Rendez-vous"
              value={s.appointments || 0}
              trend="Internes et SIH"
            />
            <Stat
              icon={Clock3}
              label="Demandes en attente"
              value={s.pending_appointments || 0}
              trend={`${s.confirmed_appointments || 0} confirmés`}
              tone="amber"
            />
            <Stat
              icon={Stethoscope}
              label="Praticiens"
              value={s.practitioners || 0}
              trend={`${s.availability || 0} disponibilités`}
              tone="teal"
            />
            <Stat
              icon={Users}
              label="Membres de l’équipe"
              value={s.members || 0}
              trend={overview.facility?.name}
              tone="violet"
            />
          </>
        )}
      </div>
      {admin && (
        <>
          <SubscriptionStats overview={overview} />
          <section className="card scope-card">
            <div className="card-head">
              <div>
                <h2>File de validation</h2>
                <p>
                  Le rôle plateforme est limité au contrôle et à l’approbation.
                </p>
              </div>
              <button
                className="primary"
                onClick={() => setPage("validations")}
              >
                Examiner {s.pending_validations || 0} demande(s)
              </button>
            </div>
            <div className="notice">
              <ShieldCheck />
              <p>
                <strong>Aucune action métier autorisée</strong>
                <span>
                  Les rendez-vous, agendas et équipes opérationnelles sont
                  administrés exclusivement par les utilisateurs rattachés aux
                  établissements.
                </span>
              </p>
            </div>
          </section>
        </>
      )}
      {!admin && (
        <>
          <DoctorAppointmentStats
            rows={overview.doctor_appointment_statistics || []}
          />
          <section className="card">
            <div className="card-head">
              <div>
                <h2>Rendez-vous enregistrés</h2>
                <p>Données réelles de {overview.facility?.name}</p>
              </div>
              <button className="link" onClick={() => setPage("appointments")}>
                Voir tous les rendez-vous <ChevronRight size={15} />
              </button>
            </div>
            <AppointmentTable
              rows={(overview.appointments || []).slice(0, 5)}
            />
          </section>
        </>
      )}
    </>
  );
}

export function eligiblePractitioners(item, practitioners, availability) {
  return practitioners.filter((practitioner) =>
    (!item.service || practitioner.specialty === item.service) &&
    availability.some((slot) => slot.available &&
      String(slot.practitioner_id) === String(practitioner.id) &&
      new Date(slot.starts_at).getTime() === new Date(item.scheduled_at).getTime()),
  );
}

export function facilityPractitioners(practitioners) {
  return [...practitioners].sort((left, right) =>
    String(left.name || "").localeCompare(String(right.name || ""), "fr", {
      sensitivity: "base",
    }),
  );
}

export function appointmentRescheduleSlots(item, availability, serviceAvailability, now = new Date()) {
  const serviceRequest = !item.practitioner_id;
  return (serviceRequest ? serviceAvailability : availability).filter((slot) =>
    slot.available && new Date(slot.starts_at) > now &&
    (serviceRequest ? slot.service === item.service : String(slot.practitioner_id) === String(item.practitioner_id)),
  );
}

function AppointmentTable({ rows = [], onAction, onAssign, onReschedule, onCancel, practitioners = [], availability = [] }) {
  useEffect(() => {
    const closeMenus = (except = null) => {
      document.querySelectorAll(".appointment-action-menu[open]").forEach((menu) => {
        if (menu !== except) menu.removeAttribute("open");
      });
    };
    const handlePointerDown = (event) => {
      const currentMenu = event.target.closest(".appointment-action-menu");
      closeMenus(currentMenu);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeMenus();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const label = {
    PENDING: "En attente",
    CONFIRMED: "Confirmé",
    COMPLETED: "Terminé",
    CANCELLED: "Annulé",
    REJECTED: "Refusé",
  };
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Rendez-vous</th>
            <th>Patient</th>
            <th>Service et motif</th>
            <th>Médecin</th>
            <th>Statut</th>
            {onAction && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i}>
              <td>
                <div className="appointment-date-cell">
                  <span>{r.scheduled_at ? new Date(r.scheduled_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}</span>
                  <div>
                    <strong>{r.scheduled_at ? new Date(r.scheduled_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : r.time}</strong>
                    <small>{r.type || "Consultation"}</small>
                  </div>
                </div>
              </td>
              <td>
                <div className="person">
                  <span>
                    {r.patient
                      .split(" ")
                      .map((x) => x[0])
                      .join("")
                      .slice(0, 2)}
                  </span>
                  <strong>{r.patient}</strong>
                </div>
              </td>
              <td>
                <span className="appointment-service">{r.service || "Consultation"}</span>
                <small className="appointment-reason">{r.reason || "Motif non renseigné"}</small>
              </td>
              <td>
                <div className={`doctor-cell ${r.practitioner_id ? "assigned" : "waiting"}`}>
                  <Stethoscope size={15} />
                  <span>{r.doctor}</span>
                </div>
              </td>
              <td>
                <Badge>{label[r.status] || r.status}</Badge>
              </td>
              {onAction && (
                <td>
                  {String(r.id).startsWith("EXT-") ? (
                    <span className="muted-action">Géré par le SIH</span>
                  ) : ["COMPLETED", "CANCELLED", "REJECTED"].includes(
                      r.status,
                    ) ? (
                    <span className="muted-action">Traitement terminé</span>
                  ) : (
                    <details className="appointment-action-menu">
                      <summary aria-label={`Actions pour le rendez-vous de ${r.patient}`} title="Actions">
                        <MoreVertical size={19} />
                      </summary>
                      <div
                        className="appointment-action-popover"
                        onClick={(event) => {
                          if (event.target.closest("button")) {
                            event.currentTarget.closest("details")?.removeAttribute("open");
                          }
                        }}
                      >
                        {!r.practitioner_id && (
                          <button onClick={() => onAssign(r)}>
                            <UserCog size={15} /> Affecter un médecin
                          </button>
                        )}
                      {r.status === "PENDING" && (
                        <>
                          {r.practitioner_id && (
                            <button onClick={() => onAction(r, "CONFIRM")}>
                              <Check size={15} /> Valider
                            </button>
                          )}
                          <button
                            className="danger-text"
                            onClick={() => onAction(r, "REJECT")}
                          >
                            <X size={15} /> Refuser
                          </button>
                        </>
                      )}
                      <button onClick={() => onReschedule(r)}>
                        <CalendarDays size={15} /> Reprogrammer
                      </button>
                      <button className="cancel" onClick={() => onCancel(r)}>
                        <X size={15} /> Annuler
                      </button>
                      </div>
                    </details>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && (
        <div className="empty-state">
          Aucune donnée enregistrée pour ce périmètre.
        </div>
      )}
    </div>
  );
}

function AssignmentModal({ item, practitioners, availability, onClose, onConfirm }) {
  const [selected, setSelected] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const filteredPractitioners = facilityPractitioners(practitioners).filter(
    (practitioner) =>
      [practitioner.name, practitioner.specialty, practitioner.phone]
        .join(" ")
        .toLocaleLowerCase("fr")
        .includes(query.trim().toLocaleLowerCase("fr")),
  );
  const selectedHasAvailableSlot = !selected || availability.some(
    (slot) => slot.available &&
      String(slot.practitioner_id) === selected &&
      new Date(slot.starts_at).getTime() === new Date(item.scheduled_at).getTime(),
  );
  return (
    <div
      className="slot-picker-overlay"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="assignment-dialog">
        <header>
          <div>
            <span className="modal-kicker">AFFECTATION</span>
            <h3>Affecter un médecin</h3>
            <p>{item.service || "Service"} · {item.patient}</p>
          </div>
          <IconButton onClick={onClose}><X /></IconButton>
        </header>
        <div className="assignment-context">
          <div><CalendarDays size={17} /><span><small>Date et heure</small><strong>{new Date(item.scheduled_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</strong></span></div>
          <div><Stethoscope size={17} /><span><small>Service demandé</small><strong>{item.service || "Non renseigné"}</strong></span></div>
        </div>
        <div className="assignment-body">
          {error && (
            <div className="assignment-error" role="alert">
              <CircleHelp size={18} />
              <span><strong>Affectation impossible</strong>{error}</span>
            </div>
          )}
          {selected && !selectedHasAvailableSlot && !error && (
            <div className="assignment-info" role="status">
              <CircleHelp size={19} />
              <span>
                <strong>Création automatique d’un créneau</strong>
                Ce médecin n’a pas de créneau disponible à cette heure. En confirmant,
                ElikyaCare créera automatiquement le créneau puis lui affectera le patient.
              </span>
            </div>
          )}
          {practitioners.length ? (
            <div className="doctor-choice-list">
              <label className="doctor-search">
                <Search size={17} />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Rechercher par nom, spécialité ou téléphone"
                  autoFocus
                />
              </label>
              <p>{practitioners.length} médecin(s) rattaché(s) à l’établissement</p>
              {filteredPractitioners.map((practitioner) => (
                <button
                  type="button"
                  className={String(practitioner.id) === selected ? "selected" : ""}
                  onClick={() => {
                    setSelected(String(practitioner.id));
                    setError("");
                  }}
                  key={practitioner.id}
                >
                  <span className="doctor-choice-avatar">{practitioner.name.split(" ").map((part) => part[0]).join("").slice(0, 2)}</span>
                  <span><strong>{practitioner.name}</strong><small>{practitioner.specialty}</small></span>
                  {String(practitioner.id) === selected ? <Check size={18} /> : <ChevronRight size={18} />}
                </button>
              ))}
              {!filteredPractitioners.length && (
                <div className="doctor-search-empty">Aucun médecin ne correspond à cette recherche.</div>
              )}
            </div>
          ) : (
            <div className="assignment-empty-state">
              <span><Stethoscope size={30} /></span>
              <h4>Aucun Médecin pour ce service</h4>
              <p>Ajoutez un médecin à ce service et renseignez ses disponibilités, ou reprogrammez le rendez-vous.</p>
            </div>
          )}
        </div>
        <footer>
          <button type="button" className="subtle" onClick={onClose}>Retour</button>
          <button
            type="button"
            className="primary"
            disabled={!selected || busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              const result = await onConfirm(selected, !selectedHasAvailableSlot);
              setBusy(false);
              if (!result?.ok) setError(result?.message || "Une erreur est survenue.");
            }}
          >
            {busy
              ? "Affectation en cours…"
              : !selectedHasAvailableSlot
                ? "Confirmer et créer le créneau"
                : "Confirmer l’affectation"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function RescheduleModal({ item, availability, serviceAvailability, onClose, onConfirm }) {
  const [selected, setSelected] = useState("");
  const serviceRequest = !item.practitioner_id;
  const slots = appointmentRescheduleSlots(item, availability, serviceAvailability);
  const grouped = slots.reduce((result, slot) => {
    const key = new Date(slot.starts_at).toISOString().slice(0, 10);
    result[key] = [...(result[key] || []), slot];
    return result;
  }, {});
  const chosen = slots.find((s) => String(s.id) === selected);
  return (
    <div
      className="slot-picker-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="slot-picker-dialog">
        <header>
          <div>
            <span className="modal-kicker">REPROGRAMMATION</span>
            <h3>Choisir un nouveau créneau</h3>
            <p>
              {item.doctor} · rendez-vous de {item.patient}
            </p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </header>
        <div className="slot-picker-legend">
          <span>
            <i />
            Créneaux disponibles
          </span>
          <b>{slots.length} disponibilité(s)</b>
        </div>
        <div className="slot-days">
          {Object.entries(grouped).map(([day, daySlots]) => (
            <section key={day}>
              <div className="slot-day-label">
                <CalendarDays />
                <div>
                  <strong>
                    {new Date(`${day}T12:00:00`).toLocaleDateString("fr-FR", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    })}
                  </strong>
                  <small>{daySlots.length} créneau(x)</small>
                </div>
              </div>
              <div className="slot-time-grid">
                {daySlots.map((slot) => (
                  <button
                    type="button"
                    className={String(slot.id) === selected ? "selected" : ""}
                    key={slot.id}
                    onClick={() => setSelected(String(slot.id))}
                  >
                    <Clock3 />
                    <strong>
                      {new Date(slot.starts_at).toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </strong>
                    <small>
                      {Math.round(
                        (new Date(slot.ends_at) - new Date(slot.starts_at)) /
                          60000,
                      )}{" "}
                      min
                    </small>
                    {String(slot.id) === selected && <Check />}
                  </button>
                ))}
              </div>
            </section>
          ))}
          {!slots.length && (
            <div className="empty-state">
              Aucun créneau futur disponible pour ce médecin.
            </div>
          )}
        </div>
        <footer>
          <div>
            {chosen ? (
              <>
                <Check />
                <span>
                  <strong>Nouveau créneau</strong>
                  {new Date(chosen.starts_at).toLocaleString("fr-FR", {
                    dateStyle: "long",
                    timeStyle: "short",
                  })}
                </span>
              </>
            ) : (
              <span>Sélectionnez une nouvelle date et heure.</span>
            )}
          </div>
          <button
            type="button"
            className="primary"
            disabled={!selected}
            onClick={() => onConfirm(selected, serviceRequest)}
          >
            Confirmer la reprogrammation
          </button>
        </footer>
      </div>
    </div>
  );
}

function CancellationModal({ item, onClose, onConfirm }) {
  const reasons = [
    "Demande du patient",
    "Patient indisponible",
    "Médecin indisponible",
    "Contraintes de l’établissement",
    "Doublon de rendez-vous",
    "Urgence prioritaire",
    "Autre",
  ];
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const finalReason = reason === "Autre" ? details.trim() : reason;
  return (
    <div
      className="slot-picker-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="decision-dialog">
        <header>
          <div>
            <span className="modal-kicker danger">ANNULATION</span>
            <h3>Motif de l’annulation</h3>
            <p>
              Rendez-vous de {item.patient} avec {item.doctor}
            </p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </header>
        <div className="decision-body">
          <p>
            Le motif sera communiqué au patient et conservé dans le journal
            d’audit.
          </p>
          <div className="reason-grid">
            {reasons.map((value) => (
              <button
                type="button"
                className={reason === value ? "selected" : ""}
                onClick={() => setReason(value)}
                key={value}
              >
                <span>{reason === value && <Check />}</span>
                {value}
              </button>
            ))}
          </div>
          {reason === "Autre" && (
            <Field label="Précisez le motif">
              <textarea
                autoFocus
                rows="3"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Saisissez un motif clair…"
              />
            </Field>
          )}
        </div>
        <footer>
          <button type="button" className="subtle" onClick={onClose}>
            Retour
          </button>
          <button
            type="button"
            className="danger-button"
            disabled={!finalReason}
            onClick={() => onConfirm(finalReason)}
          >
            Confirmer l’annulation
          </button>
        </footer>
      </div>
    </div>
  );
}

function Appointments({ overview, onRefresh }) {
  const [filter, setFilter] = useState("Tous");
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState("");
  const [rescheduleItem, setRescheduleItem] = useState(null);
  const [cancelItem, setCancelItem] = useState(null);
  const [assignmentItem, setAssignmentItem] = useState(null);
  const codes = {
    "En attente": "PENDING",
    Confirmé: "CONFIRMED",
    Terminé: "COMPLETED",
    Annulé: "CANCELLED",
    Refusé: "REJECTED",
  };
  const all = overview.appointments || [];
  const filteredByStatus =
    filter === "Tous" ? all : all.filter((x) => x.status === codes[filter]);
  const rows = filteredByStatus.filter((item) =>
    JSON.stringify(item).toLowerCase().includes(query.trim().toLowerCase()),
  );
  async function action(item, actionName, availabilityId, suppliedReason = "", practitionerId = null, serviceAvailabilityId = null, confirmCreateSlot = false) {
    const reason = ["REJECT", "CANCEL"].includes(actionName)
      ? suppliedReason ||
        window.prompt("Indiquez le motif communiqué au patient :")
      : "";
    if (["REJECT", "CANCEL"].includes(actionName) && !reason) return;
    try {
      const response = await fetch(
        `${API_BASE}/platform/backoffice/facility/appointments/${item.id}/action/`,
        {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: actionName,
          facility_id: overview.facility?.id,
          reason,
          availability_id: availabilityId,
          service_availability_id: serviceAvailabilityId,
          practitioner_id: practitionerId,
          confirm_create_slot: confirmCreateSlot,
        }),
        },
      );
      const body = await response.json();
      if (!response.ok) {
        const errorMessage = body.detail || "Action impossible.";
        setMessage(errorMessage);
        return { ok: false, message: errorMessage };
      }
      setMessage("Le médecin a été affecté et les personnes concernées ont été notifiées.");
      onRefresh();
      return { ok: true };
    } catch (_) {
      const errorMessage = "Le serveur ne répond pas. Vérifiez votre connexion puis réessayez.";
      setMessage(errorMessage);
      return { ok: false, message: errorMessage };
    }
  }
  return (
    <section className="card">
      <Toolbar
        tabs={["Tous", "En attente", "Confirmé", "Terminé", "Annulé", "Refusé"]}
        active={filter}
        setActive={setFilter}
        query={query}
        setQuery={setQuery}
      />
      {message && <div className="action-feedback">{message}</div>}
      <AppointmentTable
        rows={rows}
        practitioners={overview.practitioners || []}
        availability={overview.availability || []}
        onAction={action}
        onAssign={setAssignmentItem}
        onReschedule={setRescheduleItem}
        onCancel={setCancelItem}
      />
      <TableFooter count={rows.length} />
      {rescheduleItem && (
        <RescheduleModal
          item={rescheduleItem}
          availability={overview.availability || []}
          serviceAvailability={overview.service_availability || []}
          onClose={() => setRescheduleItem(null)}
          onConfirm={async (slotId, serviceRequest) => {
            await action(rescheduleItem, "RESCHEDULE", serviceRequest ? null : slotId, "", null, serviceRequest ? slotId : null);
            setRescheduleItem(null);
          }}
        />
      )}
      {assignmentItem && (
        <AssignmentModal
          item={assignmentItem}
          practitioners={facilityPractitioners(overview.practitioners || [])}
          availability={overview.availability || []}
          onClose={() => setAssignmentItem(null)}
          onConfirm={async (practitionerId, confirmCreateSlot) => {
            const result = await action(assignmentItem, "ASSIGN", null, "", practitionerId, null, confirmCreateSlot);
            if (result?.ok) setAssignmentItem(null);
            return result;
          }}
        />
      )}
      {cancelItem && (
        <CancellationModal
          item={cancelItem}
          onClose={() => setCancelItem(null)}
          onConfirm={async (reason) => {
            await action(cancelItem, "CANCEL", null, reason);
            setCancelItem(null);
          }}
        />
      )}
    </section>
  );
}

function SlotCreateModal({ overview, editSlot = null, onClose, onSaved }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [targetType, setTargetType] = useState(editSlot?.targetType === "Médecin" ? "DOCTOR" : "SERVICE");
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const data = Object.fromEntries(new FormData(e.currentTarget));
    const payload = {
      facility_id: overview.facility?.id,
      practitioner_id: targetType === "DOCTOR" ? data.practitioner_id : null,
      service_name: targetType === "SERVICE" ? data.service_name : "",
      starts_at: new Date(`${data.date}T${data.start_time}`).toISOString(),
      ends_at: new Date(`${data.date}T${data.end_time}`).toISOString(),
      duration_minutes: Number(data.duration_minutes),
      consultation_type: data.consultation_type,
      recurrence: data.recurrence || "NONE",
      recurrence_count: Number(data.recurrence_count || 1),
      apply_to_series: data.apply_to_series === "on",
      availability_type: targetType,
    };
    const response = await fetch(
      `${API_BASE}/platform/backoffice/facility/availability/${editSlot ? `${editSlot.id}/` : ""}`,
      {
        method: editSlot ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );
    const body = await response.json();
    setBusy(false);
    if (!response.ok) return setError(body.detail || "Création impossible.");
    onSaved(editSlot ? "Le créneau a été modifié." :
      `${body.created_count} créneau(x) créé(s)${body.skipped_count ? `, ${body.skipped_count} conflit(s) ignoré(s)` : ""}.`);
  }
  const today = new Date().toISOString().slice(0, 10);
  const localParts = (value) => {
    if (!value) return { date: today, time: "08:00" };
    const date = new Date(value);
    const pad = (number) => String(number).padStart(2, "0");
    return { date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`, time: `${pad(date.getHours())}:${pad(date.getMinutes())}` };
  };
  const initialStart = localParts(editSlot?.starts_at);
  const initialEnd = localParts(editSlot?.ends_at);
  return (
    <div className="modal-backdrop">
      <form className="modal slot-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>{editSlot ? `Modifier le créneau ${targetType === "DOCTOR" ? "du médecin" : "de service"}` : "Créer une plage de consultation"}</h2>
            <p>{editSlot ? "La modification est réservée aux créneaux qui ne sont pas encore réservés." : "Seuls les créneaux réellement libres seront ajoutés."}</p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="modal-body">
          {error && <div className="login-error">{error}</div>}
          <div className="form-grid">
            {!editSlot && <Field label="Type d’agenda">
              <select value={targetType} onChange={(e) => setTargetType(e.target.value)}>
                <option value="SERVICE">Service médical</option>
                <option value="DOCTOR">Médecin</option>
              </select>
            </Field>}
            {targetType === "SERVICE" ? <Field label="Service médical">
              <select name="service_name" defaultValue={editSlot?.service || ""} required>
                <option value="">Sélectionner…</option>
                {(overview.facility?.services || []).map((service) => <option key={service}>{service}</option>)}
              </select>
            </Field> : <Field label="Médecin">
              <select name="practitioner_id" defaultValue={editSlot?.practitioner_id || ""} required disabled={Boolean(editSlot)}>
                <option value="">Sélectionner…</option>
                {(overview.practitioners || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.specialty}
                  </option>
                ))}
              </select>
            </Field>}
            <Field label="Établissement concerné">
              <input value={overview.facility?.name || ""} readOnly />
            </Field>
            <Field label="Date">
              <input
                name="date"
                type="date"
                min={today}
                defaultValue={initialStart.date}
                required
              />
            </Field>
            <Field label="Début">
              <input
                name="start_time"
                type="time"
                defaultValue={initialStart.time}
                required
              />
            </Field>
            <Field label="Fin">
              <input
                name="end_time"
                type="time"
                defaultValue={initialEnd.time}
                required
              />
            </Field>
            {!editSlot && <Field label="Durée d’un créneau">
              <select name="duration_minutes" defaultValue="30">
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 heure</option>
              </select>
            </Field>}
            {!editSlot && <Field label="Répétition">
              <select name="recurrence" defaultValue="NONE">
                <option value="NONE">Une seule fois</option>
                <option value="WEEKLY">Chaque semaine</option>
                <option value="MONTHLY">Chaque mois</option>
              </select>
            </Field>}
            {!editSlot && <Field label="Nombre de périodes">
              <input name="recurrence_count" type="number" min="1" max="52" defaultValue="1" />
            </Field>}
            {editSlot && <label className="checkbox-line">
              <input name="apply_to_series" type="checkbox" />
              Appliquer à tous les créneaux futurs de cette série
            </label>}
            <Field label="Type">
              <select name="consultation_type" defaultValue={editSlot?.type || "CABINET"}>
                <option value="CABINET">Au cabinet</option>
                <option value="VIDEO">Téléconsultation</option>
                <option value="HOME">À domicile</option>
              </select>
            </Field>
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="subtle" onClick={onClose}>
            Annuler
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "Vérification…" : editSlot ? "Enregistrer" : "Créer les créneaux"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Agenda({ overview, onRefresh }) {
  const [target, setTarget] = useState("Tous");
  const [weekOffset, setWeekOffset] = useState(0);
  const [open, setOpen] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [message, setMessage] = useState("");
  const allRows = [
    ...(overview.availability || []).map((row) => ({ ...row, target: row.doctor, targetType: "Médecin" })),
    ...(overview.service_availability || []).map((row) => ({ ...row, target: row.service, targetType: "Service" })),
  ];
  const rows = allRows.filter((r) => target === "Tous" || r.target === target);
  const monday = new Date();
  monday.setHours(0, 0, 0, 0);
  monday.setDate(
    monday.getDate() - ((monday.getDay() + 6) % 7) + weekOffset * 7,
  );
  const days = Array.from({ length: 7 }, (_, index) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + index);
    return day;
  });
  const inWeek = rows.filter((r) => {
    const d = new Date(r.starts_at);
    return d >= days[0] && d < new Date(days[6].getTime() + 86400000);
  });
  const free = inWeek.filter((r) => r.available).length;
  const canManage = (overview.permissions || []).includes("agenda.manage");
  const facilityServices = overview.facility?.services || [];
  return (
    <>
      <section className="schedule-shell">
        <div className="schedule-top">
          <div>
            <span className="eyebrow">PLANIFICATION MÉDICALE</span>
            <h2>Agenda des médecins et services</h2>
            <p>{overview.facility?.name} · disponibilité en temps réel</p>
          </div>
          {canManage && (
            <button className="primary" onClick={() => setOpen(true)}>
              <Plus size={18} />
              Définir les créneaux
            </button>
          )}
        </div>
        {message && <div className="action-feedback">{message}</div>}
        {canManage && (
          <div className={`agenda-guidance ${facilityServices.length ? "ready" : "warning"}`}>
            <CalendarDays size={20} />
            <div>
              <strong>Horaires des services</strong>
              <span>
                {facilityServices.length
                  ? `Cliquez sur « Définir les créneaux », choisissez « Service médical », puis renseignez le service, la date et les heures.`
                  : "Aucun service n’est attribué à cet établissement. Un administrateur plateforme doit d’abord ajouter les services dans Établissements."}
              </span>
            </div>
            {facilityServices.length > 0 && (
              <button className="subtle" type="button" onClick={() => setOpen(true)}>
                Configurer
              </button>
            )}
          </div>
        )}
        <div className="schedule-controls">
          <div className="week-nav">
            <button onClick={() => setWeekOffset((x) => x - 1)}>
              <ChevronLeft />
            </button>
            <button className="today" onClick={() => setWeekOffset(0)}>
              Aujourd’hui
            </button>
            <button onClick={() => setWeekOffset((x) => x + 1)}>
              <ChevronRight />
            </button>
            <strong>
              {days[0].toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "long",
              })}{" "}
              —{" "}
              {days[6].toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </strong>
          </div>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option>Tous</option>
            {[...new Set(allRows.map((x) => x.target))].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </div>
        <div className="schedule-stats">
          <div>
            <span className="stat-dot free" />
            <strong>{free}</strong>
            <small>créneaux libres</small>
          </div>
          <div>
            <span className="stat-dot occupied" />
            <strong>{inWeek.length - free}</strong>
            <small>créneaux occupés</small>
          </div>
          <div className="legend">
            <span>
              <i className="free" />
              Libre
            </span>
            <span>
              <i className="occupied" />
              Occupé
            </span>
          </div>
        </div>
        <div className="calendar-grid">
          {days.map((day) => {
            const slots = inWeek.filter(
              (r) =>
                new Date(r.starts_at).toDateString() === day.toDateString(),
            );
            return (
              <article
                className={`calendar-day ${day.toDateString() === new Date().toDateString() ? "current" : ""}`}
                key={day.toISOString()}
              >
                <header>
                  <span>
                    {day.toLocaleDateString("fr-FR", { weekday: "short" })}
                  </span>
                  <strong>{day.getDate()}</strong>
                </header>
                <div className="day-slots">
                  {slots.map((slot) => (
                    <div
                      className={`slot-card ${slot.available ? "free" : "occupied"}`}
                      key={`${slot.targetType}-${slot.id}`}
                    >
                      <div>
                        <Clock3 size={14} />
                        {new Date(slot.starts_at).toLocaleTimeString("fr-FR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        –
                        {new Date(slot.ends_at).toLocaleTimeString("fr-FR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                      <strong>{slot.target}</strong>
                      <small>
                        {slot.targetType} · {slot.available ? "Créneau libre" : "Créneau occupé"} ·{" "}
                        {slot.type}
                      </small>
                      {canManage && slot.available && (
                        <button className="slot-edit" type="button" onClick={() => setEditSlot(slot)} title="Modifier ce créneau">
                          <Pencil size={14} /> Modifier
                        </button>
                      )}
                    </div>
                  ))}
                  {!slots.length && (
                    <span className="no-slot">Aucune plage</span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
      {open && (
        <SlotCreateModal
          overview={overview}
          onClose={() => setOpen(false)}
          onSaved={(text) => {
            setOpen(false);
            setMessage(text);
            onRefresh();
          }}
        />
      )}
      {editSlot && (
        <SlotCreateModal
          overview={overview}
          editSlot={editSlot}
          onClose={() => setEditSlot(null)}
          onSaved={(text) => {
            setEditSlot(null);
            setMessage(text);
            onRefresh();
          }}
        />
      )}
    </>
  );
}

function Toolbar({ tabs, active, setActive, query = "", setQuery }) {
  return (
    <div className="toolbar">
      <div className="tabs">
        {tabs.map((t) => (
          <button
            className={active === t ? "active" : ""}
            onClick={() => setActive(t)}
            key={t}
          >
            {t}
            {t === "En attente" && <b>2</b>}
          </button>
        ))}
      </div>
      <div className="toolbar-actions">
        <label>
          <Search size={16} />
          <input value={query} onChange={(e) => setQuery?.(e.target.value)} placeholder="Rechercher…" />
        </label>
        <button className="subtle">
          <Filter size={16} />
          Filtres
        </button>
        <button className="subtle">
          <Download size={16} />
          Exporter
        </button>
      </div>
    </div>
  );
}
function TableFooter({ count }) {
  return (
    <div className="table-footer">
      <span>
        {count} résultat{count > 1 ? "s" : ""}
      </span>
      <div>
        <button disabled>
          <ChevronLeft size={16} />
        </button>
        <b>1</b>
        <button disabled>
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function UsersPage({ overview, onRefresh }) {
  const [q, setQ] = useState("");
  const raw =
    overview.mode === "PLATFORM_ADMIN"
      ? overview.users || []
      : overview.members || [];
  const data = raw.filter((x) =>
    JSON.stringify(x).toLowerCase().includes(q.toLowerCase()),
  );
  async function toggle(user) {
    const response = await fetch(
      `${API_BASE}/platform/backoffice/admin/users/${user.id}/`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          active: !user.active,
          reason: "Mise à jour depuis le backoffice",
        }),
      },
    );
    if (response.ok) onRefresh();
  }
  return (
    <section className="card">
      <div className="toolbar">
        <div>
          <strong>
            {overview.mode === "PLATFORM_ADMIN"
              ? "Tous les comptes ElikyaCare et établissements"
              : "Équipe rattachée"}
          </strong>
        </div>
        <div className="toolbar-actions">
          <label>
            <Search size={16} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, email ou téléphone…"
            />
          </label>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Coordonnées</th>
              <th>Rôle</th>
              <th>Périmètre</th>
              <th>Statut</th>
              {overview.mode !== "PLATFORM_ADMIN" && <th>Présence</th>}
              {overview.mode === "PLATFORM_ADMIN" && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {data.map((u, i) => {
              const name =
                u.name ||
                `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                u.phone_number;
              return (
                <tr key={u.id || i}>
                  <td>
                    <strong>{name}</strong>
                  </td>
                  <td>
                    <strong className="cell-main">{u.email || "—"}</strong>
                    <small>{u.phone || u.phone_number}</small>
                  </td>
                  <td>
                    <Badge>
                      {u.profile || u.type || u.role?.name || "Utilisateur"}
                    </Badge>
                  </td>
                  <td>
                    {u.memberships?.length
                      ? u.memberships
                          .map((m) => `${m.facility} · ${m.role}`)
                          .join(", ")
                      : overview.facility?.name || "Plateforme"}
                  </td>
                  <td>
                    <Badge>
                      {u.status || (u.active ? "Actif" : "Désactivé")}
                    </Badge>
                  </td>
                  {overview.mode !== "PLATFORM_ADMIN" && (
                    <td><Badge>{u.is_online ? "En ligne" : "Hors ligne"}</Badge></td>
                  )}
                  {overview.mode === "PLATFORM_ADMIN" && (
                    <td>
                      <button
                        className={`subtle compact ${u.active ? "danger-action" : ""}`}
                        onClick={() => toggle(u)}
                      >
                        {u.active ? "Désactiver" : "Activer"}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!data.length && (
          <div className="empty-state">
            Aucun utilisateur dans ce périmètre.
          </div>
        )}
      </div>
      <TableFooter count={data.length} />
    </section>
  );
}

const perms = [
  "Voir le tableau de bord",
  "Gérer les rendez-vous",
  "Gérer l’agenda",
  "Gérer les praticiens",
  "Créer des utilisateurs",
  "Attribuer des privilèges",
  "Exporter les données",
  "Consulter l’audit",
];
function Roles({ overview }) {
  const permissionLabels = {
    "dashboard.view": "Voir le tableau de bord",
    "appointments.view": "Voir les rendez-vous",
    "appointments.manage": "Gérer les rendez-vous",
    "agenda.view": "Voir l’agenda",
    "agenda.manage": "Gérer l’agenda",
    "practitioners.view": "Voir les praticiens",
    "practitioners.manage": "Gérer les praticiens",
    "members.view": "Voir les utilisateurs",
    "members.manage": "Créer et gérer les utilisateurs",
    "roles.manage": "Gérer les rôles",
    "exports.create": "Exporter les données",
    "audit.view": "Consulter l’audit",
  };
  const roles = overview.roles || [];
  return (
    <div className="roles-grid">
      {roles.map((r, i) => (
        <section className="card role-card" key={r.id}>
          <div className="role-title">
            <span className={`role-symbol c${i}`}>
              <LockKeyhole />
            </span>
            <div>
              <h2>{r.name}</h2>
              <p>{r.description}</p>
            </div>
            <IconButton>
              <MoreHorizontal />
            </IconButton>
          </div>
          <div className="permission-list">
            {r.permissions.map((p) => (
              <div key={p} className="enabled">
                <span>
                  <Check />
                </span>
                {permissionLabels[p] || p}
              </div>
            ))}
          </div>
          <span className="role-source">
            Référentiel PostgreSQL · {overview.facility?.name}
          </span>
        </section>
      ))}
    </div>
  );
}

const headings = {
  practitioners: ["Praticien", "Spécialité", "Établissement", "Statut"],
  validations: ["Professionnel", "Type", "Structure", "Dossier", "Statut"],
  facilities: ["Établissement", "Type", "Localisation", "Équipe", "Statut"],
  pharmacies: ["Pharmacie", "Localisation", "Horaires", "Garde", "Statut"],
  ambulances: ["Service", "Zone", "Flotte", "Situation", "Statut"],
  catalog: ["Libellé", "Référentiel", "Utilisation", "Statut"],
  notifications: ["Communication", "Canal", "Audience", "Diffusion", "Statut"],
  audit: ["Horodatage", "Auteur", "Action", "Cible", "Résultat"],
};
function GenericTable({ page, overview }) {
  const [query, setQuery] = useState("");
  let headers = [];
  let rows = [];
  if (page === "validations") {
    headers = ["Professionnel", "Profil", "Téléphone", "E-mail", "Statut"];
    rows = (overview.validations || []).map((x) => [
      x.name,
      x.type,
      x.phone,
      x.email,
      x.status,
    ]);
  } else if (page === "facilities") {
    headers = ["Établissement", "Type", "Adresse", "Statut"];
    rows = (overview.facilities || []).map((x) => [
      x.name,
      x.type,
      x.address,
      x.status,
    ]);
  } else if (page === "practitioners") {
    headers = ["Praticien", "Spécialité", "Expérience", "Tarif", "Statut"];
    rows = (overview.practitioners || []).map((x) => [
      x.name,
      x.specialty,
      `${x.experience} an(s)`,
      x.fee,
      x.status,
    ]);
  } else if (page === "audit") {
    headers = [
      "Date",
      "Auteur",
      "Action",
      "Cible",
      "Établissement",
      "Résultat",
    ];
    rows = (overview.audit || []).map((x) => [
      new Date(x.created_at).toLocaleString("fr-FR"),
      x.actor,
      x.action,
      x.target,
      x.facility,
      x.status,
    ]);
  }
  rows = rows.filter((row) =>
    row.join(" ").toLowerCase().includes(query.trim().toLowerCase()),
  );
  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>{pageMeta[page][0]}</h2>
          <p>Données PostgreSQL du périmètre autorisé</p>
        </div>
      </div>
      <div className="toolbar standalone-toolbar">
        <div />
        <div className="toolbar-actions">
          <label><Search size={16} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher dans cette page…" /></label>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((v, j) => (
                  <td key={j}>
                    {j === row.length - 1 ? (
                      <Badge>{v}</Badge>
                    ) : j === 0 ? (
                      <strong>{v}</strong>
                    ) : (
                      v
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <div className="empty-state">
            Aucune donnée enregistrée pour ce module.
          </div>
        )}
      </div>
      <TableFooter count={rows.length} />
    </section>
  );
}

function PractitionerAssignmentModal({ provider, overview, onClose, onSaved }) {
  const [facilityIds, setFacilityIds] = useState([]);
  const [error, setError] = useState("");
  const candidates = (overview.facilities || []).filter(
    (f) => !(provider.facility_ids || []).includes(f.id),
  );
  async function submit(e) {
    e.preventDefault();
    const response = await fetch(
      `${API_BASE}/platform/backoffice/admin/practitioners/${provider.id}/facilities/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ facility_ids: facilityIds }),
      },
    );
    const body = await response.json();
    if (!response.ok)
      return setError(body.detail || "Rattachement impossible.");
    onSaved();
  }
  return (
    <div className="modal-backdrop">
      <form className="modal compact-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>Rattacher le médecin</h2>
            <p>
              {provider.name} possède déjà :{" "}
              {(provider.facilities || []).join(", ") || "aucun rattachement"}.
            </p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="modal-body">
          {error && <div className="login-error">{error}</div>}
          <Field label="Établissements à rattacher">
            <div className="multi-select-toolbar">
              <span>
                {facilityIds.length} établissement(s) sélectionné(s)
              </span>
              {candidates.length > 0 && (
                <button
                  type="button"
                  className="subtle compact"
                  onClick={() =>
                    setFacilityIds(
                      facilityIds.length === candidates.length
                        ? []
                        : candidates.map((facility) => facility.id),
                    )
                  }
                >
                  {facilityIds.length === candidates.length
                    ? "Tout désélectionner"
                    : "Tout sélectionner"}
                </button>
              )}
            </div>
            <div className="checkbox-list">
              {candidates.map((f) => (
                <label key={f.id} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={facilityIds.includes(f.id)}
                    onChange={(event) => setFacilityIds((current) =>
                      event.target.checked
                        ? [...current, f.id]
                        : current.filter((id) => id !== f.id)
                    )}
                  />
                  <span>{f.name}</span>
                </label>
              ))}
              {!candidates.length && (
                <p className="table-secondary">Ce médecin est déjà rattaché à tous les établissements.</p>
              )}
            </div>
          </Field>
          <div className="notice">
            <ShieldCheck />
            <p>
              <strong>Action plateforme</strong>
              <span>
                Ce rattachement donnera au médecin accès à l’agenda et aux
                consultations de cette structure.
              </span>
            </p>
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="subtle" onClick={onClose}>
            Annuler
          </button>
          <button className="primary" disabled={!facilityIds.length}>
            Rattacher à {facilityIds.length} établissement(s)
          </button>
        </div>
      </form>
    </div>
  );
}

function PractitionerCreateModal({ overview, onClose, onSaved }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const specialties = overview.facility?.services || [];
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const data = Object.fromEntries(new FormData(e.currentTarget));
    data.facility_id = overview.facility?.id;
    const response = await fetch(
      `${API_BASE}/platform/backoffice/facility/practitioners/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );
    const body = await response.json();
    setBusy(false);
    if (!response.ok)
      return setError(
        body.detail || Object.values(body)[0] || "Création impossible.",
      );
    onSaved();
  }
  return (
    <div className="modal-backdrop">
      <form className="modal practitioner-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <span className="modal-kicker">NOUVEAU PRATICIEN</span>
            <h2>Créer un médecin</h2>
            <p>
              Le praticien sera rattaché automatiquement à{" "}
              {overview.facility?.name}.
            </p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="modal-body">
          {error && <div className="login-error">{String(error)}</div>}
          <div className="form-section practitioner-identity">
            <header>
              <span>
                <Stethoscope />
              </span>
              <div>
                <h3>Identité et accès</h3>
                <p>Informations nécessaires à la connexion du médecin.</p>
              </div>
            </header>
            <div className="form-grid">
              <Field label="Prénom">
                <input name="first_name" required />
              </Field>
              <Field label="Nom">
                <input name="last_name" required />
              </Field>
              <Field label="Téléphone">
                <input name="phone_number" placeholder="+243 …" required />
              </Field>
              <Field label="Adresse e-mail">
                <input name="email" type="email" />
              </Field>
              <Field label="Mot de passe initial">
                <input name="password" type="password" minLength="8" required />
              </Field>
              <Field label="Établissement">
                <input value={overview.facility?.name || ""} readOnly />
              </Field>
            </div>
          </div>
          <div className="form-section">
            <header>
              <span>
                <ClipboardCheck />
              </span>
              <div>
                <h3>Informations professionnelles</h3>
                <p>Ces données seront soumises à la validation plateforme.</p>
              </div>
            </header>
            <div className="form-grid">
              <Field label="Spécialité">
                <select name="specialty" defaultValue="" required disabled={!specialties.length}>
                  <option value="" disabled>Sélectionner une spécialité</option>
                  {specialties.map((specialty) => (
                    <option value={specialty} key={specialty}>{specialty}</option>
                  ))}
                </select>
                {!specialties.length && (
                  <small className="field-help warning">
                    Aucun service médical n’est configuré pour cet établissement.
                  </small>
                )}
              </Field>
              <Field label="Numéro d’ordre">
                <input name="medical_order_number" />
              </Field>
              <Field label="Années d’expérience">
                <input
                  name="years_experience"
                  type="number"
                  min="0"
                  defaultValue="0"
                />
              </Field>
              <Field label="Tarif de consultation">
                <input
                  name="fee"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue="0"
                />
              </Field>
              <Field label="Présentation">
                <textarea name="bio" rows="3" />
              </Field>
            </div>
          </div>
          <div className="notice">
            <ShieldCheck />
            <p>
              <strong>Rattachement protégé</strong>
              <span>
                L’établissement ne peut rattacher ce nouveau médecin qu’à sa
                propre structure. Tout autre rattachement exige l’intervention
                de l’administrateur plateforme.
              </span>
            </p>
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="subtle" onClick={onClose}>
            Annuler
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "Création…" : "Créer le médecin"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FacilityPractitionersPage({ overview, onRefresh }) {
  const [open, setOpen] = useState(false);
  const allowed = (overview.permissions || []).includes("practitioners.manage");
  const rows = overview.practitioners || [];
  return (
    <>
      <section className="card">
        <div className="card-head">
          <div>
            <h2>Médecins de l’établissement</h2>
            <p>Praticiens rattachés à {overview.facility?.name}</p>
          </div>
          {allowed && (
            <button className="primary" onClick={() => setOpen(true)}>
              <Plus />
              Créer un médecin
            </button>
          )}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Médecin</th>
                <th>Spécialité</th>
                <th>Téléphone</th>
                <th>N° d’ordre</th>
                <th>Expérience</th>
                <th>Validation</th>
                <th>Compte</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{p.name}</strong>
                    <small className="table-secondary">{p.email || "—"}</small>
                  </td>
                  <td>{p.specialty}</td>
                  <td>{p.phone || "—"}</td>
                  <td className="mono">{p.reference || "—"}</td>
                  <td>{p.experience} an(s)</td>
                  <td>
                    <Badge>{p.approval}</Badge>
                  </td>
                  <td>
                    <Badge>
                      {p.status === "ACTIVE" ? "Actif" : "Désactivé"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <div className="empty-state">
              Aucun médecin rattaché à cet établissement.
            </div>
          )}
        </div>
        <TableFooter count={rows.length} />
      </section>
      {open && (
        <PractitionerCreateModal
          overview={overview}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
}
function ProvidersPage({ overview, onRefresh }) {
  const rows = overview.providers || [];
  const [assignment, setAssignment] = useState(null);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState("");
  async function approve(provider) {
    setBusy(`approve-${provider.id}`);
    setMessage("");
    const response = await fetch(
      `${API_BASE}/accounts/admin/approvals/${provider.id}/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ decision: "APPROVED" }),
      },
    );
    const body = await response.json();
    setBusy(null);
    if (!response.ok) {
      setMessage(body.detail || "Approbation impossible.");
      return;
    }
    setMessage(`${provider.name} a été approuvé avec succès.`);
    onRefresh();
  }
  async function toggle(provider) {
    setBusy(`toggle-${provider.id}`);
    setMessage("");
    const response = await fetch(
      `${API_BASE}/platform/backoffice/admin/users/${provider.id}/`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          active: !provider.active,
          reason: "Mise à jour du prestataire",
        }),
      },
    );
    const body = await response.json();
    setBusy(null);
    if (!response.ok) {
      setMessage(body.detail || "Mise à jour du compte impossible.");
      return;
    }
    onRefresh();
  }
  return (
    <>
      <section className="card">
        <div className="card-head">
          <div>
            <h2>Prestataires de santé</h2>
            <p>
              Médecins et services ambulanciers enregistrés dans ElikyaCare.
            </p>
          </div>
        </div>
        {message && (
          <div className="notice">
            <ShieldCheck />
            <p>
              <strong>Gestion du prestataire</strong>
              <span>{message}</span>
            </p>
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Prestataire</th>
                <th>Type</th>
                <th>Spécialité / structure</th>
                <th>Référence</th>
                <th>Rattachement</th>
                <th>Validation</th>
                <th>Compte</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={`${x.kind}-${x.id}`}>
                  <td>
                    <strong>{x.name}</strong>
                  </td>
                  <td>{x.kind}</td>
                  <td>{x.specialty}</td>
                  <td className="mono">{x.reference}</td>
                  <td>{x.facilities?.join(", ") || "—"}</td>
                  <td>
                    <Badge>{x.approval}</Badge>
                  </td>
                  <td>
                    <Badge>{x.active ? "Actif" : "Désactivé"}</Badge>
                  </td>
                  <td>
                    <div className="row-actions">
                      {x.approval !== "APPROVED" && (
                        <button
                          className="approve compact"
                          disabled={busy === `approve-${x.id}`}
                          onClick={() => approve(x)}
                        >
                          <Check />
                          {busy === `approve-${x.id}` ? "Approbation…" : "Approuver"}
                        </button>
                      )}
                      <button
                        className={`subtle compact ${x.active ? "danger-action" : ""}`}
                        disabled={busy === `toggle-${x.id}`}
                        onClick={() => toggle(x)}
                      >
                        {x.active ? "Désactiver" : "Activer"}
                      </button>
                      {x.kind === "Médecin" && (
                        <button
                          className="subtle compact"
                          onClick={() => setAssignment(x)}
                        >
                          Rattacher
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <div className="empty-state">Aucun prestataire enregistré.</div>
          )}
        </div>
        <TableFooter count={rows.length} />
      </section>
      {assignment && (
        <PractitionerAssignmentModal
          provider={assignment}
          overview={overview}
          onClose={() => setAssignment(null)}
          onSaved={() => {
            setAssignment(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
}

function AmbulanceEditModal({ provider, onClose, onSaved }) {
  const edit = Boolean(provider?.ambulance_profile_id);
  const [error, setError] = useState("");
  async function submit(e) {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    const url = edit
      ? `${API_BASE}/platform/backoffice/admin/ambulances/${provider.ambulance_profile_id}/`
      : `${API_BASE}/platform/backoffice/admin/ambulances/`;
    const response = await fetch(url, {
      method: edit ? "PUT" : "POST",
      headers: {
        Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const body = await response.json();
    if (!response.ok) {
      setError(
        body.detail || Object.values(body)[0] || "Enregistrement impossible.",
      );
      return;
    }
    onSaved();
  }
  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>
              {edit
                ? "Modifier le prestataire ambulancier"
                : "Ajouter un prestataire ambulancier"}
            </h2>
            <p>Service, agrément et zone d’intervention.</p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="modal-body">
          {error && <div className="login-error">{String(error)}</div>}
          <div className="form-grid">
            <Field label="Société / service">
              <input
                name="company_name"
                defaultValue={provider?.company_name || ""}
                required
              />
            </Field>
            <Field label="Numéro d’agrément">
              <input
                name="license_number"
                defaultValue={provider?.license_number || ""}
                required
              />
            </Field>
            <Field label="Prénom du responsable">
              <input
                name="first_name"
                defaultValue={provider?.first_name || ""}
              />
            </Field>
            <Field label="Nom du responsable">
              <input
                name="last_name"
                defaultValue={provider?.last_name || ""}
              />
            </Field>
            <Field label="Téléphone">
              <input
                name="phone_number"
                defaultValue={provider?.phone || ""}
                readOnly={edit}
                required
              />
            </Field>
            <Field label="E-mail">
              <input
                name="email"
                type="email"
                defaultValue={provider?.email || ""}
              />
            </Field>
            <Field label="Zone d’intervention">
              <input
                name="service_area"
                defaultValue={provider?.service_area || ""}
              />
            </Field>
            <Field label="Latitude GPS">
              <input
                name="latitude"
                type="number"
                step="0.000001"
                min="-90"
                max="90"
                defaultValue={provider?.latitude ?? ""}
                placeholder="Ex. -4.325000"
              />
            </Field>
            <Field label="Longitude GPS">
              <input
                name="longitude"
                type="number"
                step="0.000001"
                min="-180"
                max="180"
                defaultValue={provider?.longitude ?? ""}
                placeholder="Ex. 15.322000"
              />
            </Field>
            <Field label="Immatriculation principale">
              <input
                name="vehicle_registration"
                defaultValue={provider?.vehicle_registration || ""}
              />
            </Field>
            <Field label="Type d’ambulance">
              <input
                name="ambulance_type"
                defaultValue={provider?.ambulance_type || ""}
                placeholder="Ambulance médicalisée"
              />
            </Field>
            {!edit && (
              <Field label="Mot de passe initial">
                <input name="password" type="password" minLength="8" required />
              </Field>
            )}
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="subtle" onClick={onClose}>
            Annuler
          </button>
          <button className="primary" type="submit">
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
}

function AmbulancesPage({ overview, onRefresh }) {
  const rows = (overview.providers || []).filter(
    (x) => x.kind === "Ambulancier",
  );
  const [selected, setSelected] = useState(undefined);
  const [open, setOpen] = useState(false);
  return (
    <>
      <section className="card">
        <div className="card-head">
          <div>
            <h2>Prestataires ambulanciers</h2>
            <p>Services autorisés à recevoir les demandes d’urgence.</p>
          </div>
          <button
            className="primary"
            onClick={() => {
              setSelected(undefined);
              setOpen(true);
            }}
          >
            <Plus />
            Ajouter
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>Responsable</th>
                <th>Agrément</th>
                <th>Zone</th>
                <th>Coordonnées GPS</th>
                <th>Véhicule</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.id}>
                  <td>
                    <strong>{x.company_name}</strong>
                  </td>
                  <td>{x.name}</td>
                  <td className="mono">{x.license_number}</td>
                  <td>{x.service_area || "—"}</td>
                  <td className="mono">
                    {x.latitude != null && x.longitude != null
                      ? `${x.latitude}, ${x.longitude}`
                      : "Non renseignées"}
                  </td>
                  <td>{x.vehicle_registration || "—"}</td>
                  <td>
                    <Badge>{x.active ? "Actif" : "Désactivé"}</Badge>
                  </td>
                  <td>
                    <button
                      className="subtle compact"
                      onClick={() => {
                        setSelected(x);
                        setOpen(true);
                      }}
                    >
                      Modifier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <div className="empty-state">
              Aucun prestataire ambulancier enregistré.
            </div>
          )}
        </div>
      </section>
      {open && (
        <AmbulanceEditModal
          provider={selected}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
}

function FacilityEditModal({ facility, onClose, onSaved }) {
  const [error, setError] = useState("");
  async function submit(e) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    data.set("is_open", String(data.get("is_open") === "on"));
    data.set("is_emergency", String(data.get("is_emergency") === "on"));
    for (const key of ["latitude", "longitude"])
      if (!data.get(key)) data.delete(key);
    const response = await fetch(
      `${API_BASE}/platform/admin/facilities/${facility.id}/`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
        },
        body: data,
      },
    );
    const body = await response.json();
    if (!response.ok) {
      setError(body.detail || "Mise à jour impossible.");
      return;
    }
    const resourceFile = data.get("resource_file");
    if (resourceFile?.size) {
      const resource = new FormData();
      resource.set("file", resourceFile);
      resource.set("title", data.get("resource_title") || resourceFile.name);
      resource.set("resource_type", data.get("resource_type") || "DOCUMENT");
      const resourceResponse = await fetch(
        `${API_BASE}/platform/admin/facilities/${facility.id}/resources/`,
        { method: "POST", headers: { Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}` }, body: resource },
      );
      if (!resourceResponse.ok) {
        setError("L’établissement est enregistré, mais la ressource n’a pas pu être ajoutée.");
        return;
      }
    }
    onSaved();
  }
  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>Modifier l’établissement</h2>
            <p>Référentiel partagé avec l’application ElikyaCare.</p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="modal-body">
          {error && <div className="login-error">{error}</div>}
          <div className="form-grid">
            <Field label="Nom">
              <input name="name" defaultValue={facility.name} required />
            </Field>
            <Field label="Type">
              <select
                name="facility_type"
                defaultValue={facility.facility_type}
              >
                <option value="HOSPITAL">Hôpital</option>
                <option value="CLINIC">Clinique / cabinet</option>
                <option value="LAB">Laboratoire</option>
                <option value="IMAGING">Centre d’imagerie</option>
                <option value="PHARMACY">Pharmacie</option>
                <option value="OTHER">Autre</option>
              </select>
            </Field>
            <Field label="Adresse">
              <input name="address" defaultValue={facility.address} required />
            </Field>
            <Field label="Téléphone">
              <input name="phone" defaultValue={facility.phone} />
            </Field>
            <Field label="Services de l’établissement — cochez tous les services disponibles" wide>
              <ServiceSelector initial={facility.services || []} />
            </Field>
            <Field label="Latitude">
              <input
                name="latitude"
                type="number"
                step="any"
                defaultValue={facility.latitude || ""}
              />
            </Field>
            <Field label="Longitude">
              <input
                name="longitude"
                type="number"
                step="any"
                defaultValue={facility.longitude || ""}
              />
            </Field>
            <Field label="Photo de l’établissement">
              <input name="photo" type="file" accept="image/*" />
              {facility.photo_url && <small><a href={facility.photo_url} target="_blank" rel="noreferrer">Voir la photo actuelle</a></small>}
            </Field>
            <Field label="URL vidéo">
              <input name="video_url" type="url" defaultValue={facility.video_url || ""} placeholder="https://…" />
            </Field>
          </div>
          <div className="check-grid governance-checks">
            <label>
              <input
                name="is_open"
                type="checkbox"
                defaultChecked={facility.is_open}
              />
              <span>
                <Check />
              </span>
              Établissement actif
            </label>
            <label>
              <input
                name="is_emergency"
                type="checkbox"
                defaultChecked={facility.is_emergency}
              />
              <span>
                <Check />
              </span>
              Service d’urgence
            </label>
          </div>
          <h3>Ressources publiques</h3>
          {(facility.resources || []).map((resource) => (
            <p key={resource.id}><a href={resource.url} target="_blank" rel="noreferrer">{resource.title}</a> · {resource.type}</p>
          ))}
          <div className="form-grid">
            <Field label="Titre de la nouvelle ressource">
              <input name="resource_title" placeholder="Ex. Présentation du service" />
            </Field>
            <Field label="Type de ressource">
              <select name="resource_type" defaultValue="DOCUMENT">
                <option value="IMAGE">Image</option><option value="VIDEO">Vidéo</option>
                <option value="AUDIO">Son</option><option value="DOCUMENT">Document</option>
              </select>
            </Field>
            <Field label="Fichier">
              <input name="resource_file" type="file" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx" />
            </Field>
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="subtle" onClick={onClose}>
            Annuler
          </button>
          <button className="primary" type="submit">
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
}

function FacilityCreateModal({ onClose, onSaved }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    const data = new FormData(e.currentTarget);
    data.set("is_open", String(data.get("is_open") === "on"));
    data.set("is_emergency", String(data.get("is_emergency") === "on"));
    for (const key of ["latitude", "longitude"])
      if (!data.get(key)) data.delete(key);

    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/platform/admin/facilities/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
        },
        body: data,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const fieldError = Object.values(body).find(
          (value) => typeof value === "string",
        );
        setError(
          body.detail || fieldError || "Création de l’établissement impossible.",
        );
        return;
      }
      onSaved(body);
    } catch {
      setError("Le serveur est momentanément indisponible. Réessayez.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <form className="modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>Créer un établissement</h2>
            <p>
              Ajoutez une structure au référentiel partagé ElikyaCare.
            </p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="modal-body">
          {error && <div className="login-error">{String(error)}</div>}
          <div className="form-grid">
            <Field label="Nom de l’établissement">
              <input
                name="name"
                maxLength="180"
                placeholder="Ex. Centre médical Elikya"
                autoFocus
                required
              />
            </Field>
            <Field label="Type">
              <select name="facility_type" defaultValue="HOSPITAL" required>
                <option value="HOSPITAL">Hôpital</option>
                <option value="CLINIC">Clinique / cabinet</option>
                <option value="LAB">Laboratoire</option>
                <option value="IMAGING">Centre d’imagerie</option>
                <option value="PHARMACY">Pharmacie</option>
                <option value="OTHER">Autre</option>
              </select>
            </Field>
            <Field label="Adresse">
              <input
                name="address"
                maxLength="255"
                placeholder="Avenue, commune, ville"
                required
              />
            </Field>
            <Field label="Téléphone">
              <input
                name="phone"
                type="tel"
                maxLength="30"
                placeholder="+243 …"
              />
            </Field>
            <Field label="Services de l’établissement — cochez tous les services disponibles" wide>
              <ServiceSelector />
            </Field>
            <Field label="Latitude">
              <input
                name="latitude"
                type="number"
                step="0.000001"
                min="-90"
                max="90"
                placeholder="Ex. -4.325000"
              />
            </Field>
            <Field label="Longitude">
              <input
                name="longitude"
                type="number"
                step="0.000001"
                min="-180"
                max="180"
                placeholder="Ex. 15.322000"
              />
            </Field>
            <Field label="Photo de l’établissement">
              <input name="photo" type="file" accept="image/*" />
            </Field>
            <Field label="URL de la vidéo">
              <input name="video_url" type="url" placeholder="https://…" />
            </Field>
          </div>
          <div className="check-grid governance-checks">
            <label>
              <input name="is_open" type="checkbox" defaultChecked />
              <span>
                <Check />
              </span>
              Établissement actif
            </label>
            <label>
              <input name="is_emergency" type="checkbox" />
              <span>
                <Check />
              </span>
              Service d’urgence
            </label>
          </div>
          <div className="notice">
            <ShieldCheck />
            <p>
              <strong>Action d’administration plateforme</strong>
              <span>
                Le nouvel établissement sera immédiatement disponible dans le
                référentiel, les rattachements et les applications ElikyaCare.
              </span>
            </p>
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="subtle" onClick={onClose}>
            Annuler
          </button>
          <button className="primary" type="submit" disabled={busy}>
            {busy ? "Création…" : "Créer l’établissement"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FacilitiesPage({ overview, onRefresh }) {
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const rows = (overview.facilities || []).filter((item) =>
    JSON.stringify(item).toLowerCase().includes(query.trim().toLowerCase()),
  );
  return (
    <>
      <section className="card">
        <div className="card-head">
          <div>
            <h2>Établissements de santé</h2>
            <p>Référentiel PostgreSQL utilisé par les applications mobiles.</p>
          </div>
        </div>
        <div className="toolbar standalone-toolbar">
          <div />
          <div className="toolbar-actions">
            <label>
              <Search size={16} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nom, type, adresse ou service…" />
            </label>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Établissement</th>
                <th>Type</th>
                <th>Adresse</th>
                <th>Téléphone</th>
                <th>Services attribués</th>
                <th>Urgences</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.id}>
                  <td>
                    <strong>{x.name}</strong>
                  </td>
                  <td>{x.type}</td>
                  <td>{x.address}</td>
                  <td>{x.phone || "—"}</td>
                  <td>
                    <div className="service-summary">
                      {(x.services || []).slice(0, 3).map((service) => <span key={service}>{service}</span>)}
                      {(x.services || []).length > 3 && <b>+{x.services.length - 3}</b>}
                      {!(x.services || []).length && <em>Aucun service</em>}
                    </div>
                  </td>
                  <td>{x.is_emergency ? "Oui" : "Non"}</td>
                  <td>
                    <Badge>{x.is_open ? "Actif" : "Désactivé"}</Badge>
                  </td>
                  <td>
                    <button
                      className="subtle compact"
                      onClick={() => setSelected(x)}
                    >
                      Services et détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TableFooter count={rows.length} />
      </section>
      {selected && (
        <FacilityEditModal
          facility={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
}

function CarouselPage({ overview, onRefresh }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    const data = new FormData(event.currentTarget);
    data.set("enabled", "true");
    try {
      const response = await fetch(`${API_BASE}/platform/admin/carousel/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}` },
        body: data,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.detail || "Publication impossible.");
      event.currentTarget.reset(); onRefresh();
    } catch (caught) { setError(caught.message); }
    finally { setBusy(false); }
  }
  async function remove(id) {
    if (!window.confirm("Retirer ce contenu du carrousel ?")) return;
    await fetch(`${API_BASE}/platform/admin/carousel/${id}/`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}` },
    });
    onRefresh();
  }
  return <div className="settings-grid">
    <section className="card form-card">
      <h2>Publier dans le carrousel mobile</h2>
      <p>Le rattachement à un établissement est facultatif.</p>
      {error && <div className="login-error">{error}</div>}
      <form onSubmit={submit} className="form-grid">
        <Field label="Titre"><input name="title" maxLength="120" required /></Field>
        <Field label="Badge"><input name="badge" maxLength="40" placeholder="Information" /></Field>
        <Field label="Contenu" wide><textarea name="body" maxLength="280" rows="4" required /></Field>
        <Field label="Établissement lié (facultatif)"><select name="facility_id" defaultValue=""><option value="">Aucun établissement</option>{(overview.facilities || []).map((facility) => <option key={facility.id} value={facility.id}>{facility.name}</option>)}</select></Field>
        <Field label="Image"><input name="image" type="file" accept="image/*" /></Field>
        <Field label="Libellé de l’action"><input name="action_label" placeholder="En savoir plus" /></Field>
        <Field label="Route mobile"><input name="action_route" placeholder="/facilities/12" /></Field>
        <Field label="Couleur"><input name="background_color" type="color" defaultValue="#176B87" /></Field>
        <Field label="Priorité"><input name="priority" type="number" min="0" defaultValue="100" /></Field>
        <button className="primary" disabled={busy}>{busy ? "Publication…" : "Publier"}</button>
      </form>
    </section>
    <section className="card form-card">
      <h2>Contenus publiés</h2>
      {(overview.carousel_slides || []).map((slide) => <div className="toggle-line" key={slide.id}><div><strong>{slide.title}</strong><span>{slide.facility || "Tous les utilisateurs"} · priorité {slide.priority}</span></div><button className="subtle" onClick={() => remove(slide.id)}>Retirer</button></div>)}
      {!(overview.carousel_slides || []).length && <p>Aucun contenu personnalisé.</p>}
    </section>
  </div>;
}

function ValidationsPage({ overview, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState("");
  async function decide(id, decision) {
    setBusy(id);
    setMessage("");
    const response = await fetch(
      `${API_BASE}/accounts/admin/approvals/${id}/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ decision }),
      },
    );
    const body = await response.json();
    setBusy(null);
    if (!response.ok) {
      setMessage(body.detail || "Décision impossible.");
      return;
    }
    setMessage(body.detail);
    onRefresh();
  }
  const rows = overview.validations || [];
  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Professionnels à contrôler</h2>
          <p>Seules les décisions de validation et de rejet sont autorisées.</p>
        </div>
      </div>
      {message && (
        <div className="notice">
          <ShieldCheck />
          <p>
            <strong>Décision</strong>
            <span>{message}</span>
          </p>
        </div>
      )}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Professionnel</th>
              <th>Profil</th>
              <th>Téléphone</th>
              <th>E-mail</th>
              <th>Soumis le</th>
              <th>Décision</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={x.id}>
                <td>
                  <strong>{x.name}</strong>
                </td>
                <td>
                  <Badge>{x.type}</Badge>
                </td>
                <td>{x.phone}</td>
                <td>{x.email}</td>
                <td>{new Date(x.created_at).toLocaleString("fr-FR")}</td>
                <td>
                  <div className="decision-actions">
                    <button
                      className="approve"
                      disabled={busy === x.id}
                      onClick={() => decide(x.id, "APPROVED")}
                    >
                      <Check />
                      Approuver
                    </button>
                    <button
                      className="reject"
                      disabled={busy === x.id}
                      onClick={() => decide(x.id, "REJECTED")}
                    >
                      <X />
                      Rejeter
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <div className="empty-state">
            Aucune validation professionnelle en attente.
          </div>
        )}
      </div>
    </section>
  );
}

function SettingsPage() {
  return (
    <div className="settings-grid">
      <section className="card settings-nav">
        <button className="active">Règles générales</button>
        <button>Sécurité et sessions</button>
        <button>Notifications</button>
        <button>Localisation</button>
        <button>Intégrations</button>
      </section>
      <section className="card form-card">
        <h2>Règles générales</h2>
        <p>Paramètres appliqués par défaut à l’ensemble des structures.</p>
        <div className="form-grid">
          <Field label="Fuseau horaire">
            <select>
              <option>Africa/Kinshasa (GMT+1)</option>
            </select>
          </Field>
          <Field label="Langue principale">
            <select>
              <option>Français</option>
            </select>
          </Field>
          <Field label="Durée d’une consultation">
            <input value="30 minutes" readOnly />
          </Field>
          <Field label="Délai minimal de réservation">
            <input value="2 heures" readOnly />
          </Field>
        </div>
        <hr />
        <h3>Protection des accès</h3>
        <div className="toggle-line">
          <div>
            <strong>Authentification à deux facteurs obligatoire</strong>
            <span>Exiger un OTP pour tous les comptes administratifs.</span>
          </div>
          <label className="switch">
            <input type="checkbox" defaultChecked />
            <span />
          </label>
        </div>
        <div className="toggle-line">
          <div>
            <strong>Journaliser les exports</strong>
            <span>
              Conserver l’auteur, le périmètre et la date de chaque export.
            </span>
          </div>
          <label className="switch">
            <input type="checkbox" defaultChecked />
            <span />
          </label>
        </div>
        <div className="form-actions">
          <button className="subtle">Annuler</button>
          <button className="primary">Enregistrer les modifications</button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, wide = false }) {
  return (
    <div className={`field ${wide ? "field-wide" : ""}`}>
      <span>{label}</span>
      {children}
    </div>
  );
}
const API_BASE =
  import.meta.env.VITE_API_BASE_URL || "https://api-staging.dataphere.com/api";
function Login({ onAuthenticated }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/accounts/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number: phone,
          password,
          remember_me: true,
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.detail || "Connexion impossible.");
      if (body.requires_approval)
        throw new Error("Ce compte administratif doit encore être validé.");
      sessionStorage.setItem("kimyacare_access", body.tokens.access);
      onAuthenticated(body.user);
    } catch (e) {
      setError(e.message || "Le service est momentanément indisponible.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="login-page">
      <div className="login-brand">
        <Logo />
        <div className="login-brand-message">
          <div className="login-phone-stage" aria-hidden="true">
            <div className="login-phone-halo" />
            <div className="login-phone-orbit">
              <i />
            </div>
            <svg className="care-network-lines" viewBox="0 0 320 210">
              <path d="M70 48 C105 55 118 78 145 94" />
              <path d="M250 48 C215 55 202 78 175 94" />
              <path d="M160 190 C160 164 160 143 160 124" />
              <circle className="flow flow-one" r="4">
                <animateMotion
                  dur="2.8s"
                  repeatCount="indefinite"
                  path="M70 48 C105 55 118 78 145 94"
                />
              </circle>
              <circle className="flow flow-two" r="4">
                <animateMotion
                  dur="3.2s"
                  repeatCount="indefinite"
                  path="M250 48 C215 55 202 78 175 94"
                />
              </circle>
              <circle className="flow flow-three" r="4">
                <animateMotion
                  dur="2.5s"
                  repeatCount="indefinite"
                  path="M160 190 C160 164 160 143 160 124"
                />
              </circle>
            </svg>
            <div className="care-node patient-node">
              <span>
                <Users size={15} />
              </span>
              <b>Patient</b>
              <small>Accès aux soins</small>
            </div>
            <div className="care-node doctor-node">
              <span>
                <Stethoscope size={15} />
              </span>
              <b>Médecin</b>
              <small>Prise en charge</small>
            </div>
            <div className="care-node facility-node">
              <span>
                <Building2 size={15} />
              </span>
              <b>Établissement</b>
              <small>Coordination</small>
            </div>
            <div className="login-phone-device">
              <div className="login-phone-island" />
              <div className="login-phone-screen">
                <div className="login-phone-brand">
                  <b>♥</b>
                  <span>ElikyaCare</span>
                </div>
                <div className="login-phone-line wide" />
                <div className="login-phone-line" />
                <div className="login-phone-card">
                  <i>+</i>
                  <span />
                </div>
                <div className="login-phone-actions">
                  <i />
                  <i />
                  <i />
                </div>
              </div>
            </div>
          </div>
          <span>CONSOLE DE GOUVERNANCE SANTÉ</span>
          <h1>
            Pilotez vos structures
            <br />
            avec précision.
          </h1>
          <p>
            Une administration unifiée, sécurisée et traçable pour vos équipes,
            praticiens et parcours de soins.
          </p>
        </div>
        <small>Protection des données · Accès contrôlé · Audit continu</small>
      </div>
      <div className="login-panel">
        <form onSubmit={submit}>
          <div className="login-mobile-logo">
            <Logo />
          </div>
          <span className="eyebrow">ESPACE SÉCURISÉ</span>
          <h2>Bienvenue</h2>
          <p>Connectez-vous avec votre compte ElikyaCare autorisé.</p>
          {error && (
            <div className="login-error">
              <ShieldCheck />
              {error}
            </div>
          )}
          <Field label="Numéro de téléphone">
            <input
              autoFocus
              autoComplete="username"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+243 …"
              required
            />
          </Field>
          <Field label="Mot de passe">
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Votre mot de passe"
              required
            />
          </Field>
          <div className="login-options">
            <label>
              <input type="checkbox" /> Se souvenir de moi
            </label>
            <button type="button">Accès oublié ?</button>
          </div>
          <button className="primary login-submit" disabled={busy}>
            {busy ? "Vérification…" : "Se connecter"}
            <ChevronRight />
          </button>
          <div className="login-security">
            <LockKeyhole />
            <span>
              Connexion chiffrée. Après plusieurs tentatives incorrectes,
              l’accès est temporairement protégé.
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}

function AppointmentCreateModal({ overview, onClose, onSaved }) {
  const [patientMode, setPatientMode] = useState("existing");
  const [patientId, setPatientId] = useState("");
  const [practitionerId, setPractitionerId] = useState("");
  const [availabilityId, setAvailabilityId] = useState("");
  const [slotPickerOpen, setSlotPickerOpen] = useState(false);
  const [reason, setReason] = useState("Consultation classique");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const patients = (overview.patients || []).filter((p) =>
    `${p.name} ${p.phone}`.toLowerCase().includes(search.toLowerCase()),
  );
  const practitioner = (overview.practitioners || []).find(
    (p) => String(p.id) === practitionerId,
  );
  const slots = (overview.availability || []).filter(
    (s) =>
      s.available &&
      new Date(s.starts_at) > new Date() &&
      (!practitioner || String(s.practitioner_id) === practitionerId),
  );
  const selectedPatient = (overview.patients || []).find(
    (p) => String(p.id) === patientId,
  );
  const selectedSlot = slots.find((s) => String(s.id) === availabilityId);
  const slotsByDay = slots.reduce((groups, slot) => {
    const key = new Date(slot.starts_at).toISOString().slice(0, 10);
    groups[key] = [...(groups[key] || []), slot];
    return groups;
  }, {});
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const values = Object.fromEntries(new FormData(e.currentTarget));
    const payload = {
      facility_id: overview.facility?.id,
      patient_id: patientMode === "existing" ? patientId : null,
      patient_first_name: values.patient_first_name,
      patient_last_name: values.patient_last_name,
      patient_phone: values.patient_phone,
      practitioner_id: practitionerId,
      availability_id: availabilityId,
      reason,
      notes: values.notes,
    };
    const response = await fetch(
      `${API_BASE}/platform/backoffice/facility/appointments/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("kimyacare_access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );
    const body = await response.json();
    setBusy(false);
    if (!response.ok)
      return setError(body.detail || "Création du rendez-vous impossible.");
    onSaved();
  }
  return (
    <div className="modal-backdrop appointment-modal-backdrop">
      <form className="modal appointment-create-modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <span className="modal-kicker">ELIKYACARE · ÉTABLISSEMENT</span>
            <h2>Nouveau rendez-vous</h2>
            <p>
              Saisissez les informations du patient et réservez un créneau
              réellement disponible.
            </p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </div>
        {error && (
          <div className="appointment-alert">
            <CircleHelp />
            <div>
              <strong>Création impossible</strong>
              <span>{error}</span>
            </div>
          </div>
        )}
        <div className="appointment-form-layout">
          <div className="appointment-form-main">
            <section className="form-section">
              <header>
                <span>
                  <Users />
                </span>
                <div>
                  <h3>Patient</h3>
                  <p>
                    Sélectionnez un dossier existant ou créez une fiche
                    minimale.
                  </p>
                </div>
              </header>
              <div className="mode-switch">
                <button
                  type="button"
                  className={patientMode === "existing" ? "active" : ""}
                  onClick={() => setPatientMode("existing")}
                >
                  Patient existant
                </button>
                <button
                  type="button"
                  className={patientMode === "new" ? "active" : ""}
                  onClick={() => {
                    setPatientMode("new");
                    setPatientId("");
                  }}
                >
                  Nouveau patient
                </button>
              </div>
              {patientMode === "existing" ? (
                <>
                  <Field label="Rechercher">
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Nom ou numéro de téléphone…"
                    />
                  </Field>
                  <div className="patient-results">
                    {patients.slice(0, 6).map((p) => (
                      <button
                        type="button"
                        className={String(p.id) === patientId ? "selected" : ""}
                        onClick={() => setPatientId(String(p.id))}
                        key={p.id}
                      >
                        <span>
                          {p.name
                            .split(" ")
                            .map((x) => x[0])
                            .join("")
                            .slice(0, 2)}
                        </span>
                        <div>
                          <strong>{p.name}</strong>
                          <small>{p.phone}</small>
                        </div>
                        <Check />
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="form-grid">
                  <Field label="Prénom">
                    <input name="patient_first_name" required />
                  </Field>
                  <Field label="Nom">
                    <input name="patient_last_name" required />
                  </Field>
                  <Field label="Téléphone">
                    <input name="patient_phone" placeholder="+243 …" required />
                  </Field>
                </div>
              )}
            </section>
            <section className="form-section">
              <header>
                <span>
                  <Stethoscope />
                </span>
                <div>
                  <h3>Détails de la consultation</h3>
                  <p>
                    Les créneaux proposés proviennent de l’agenda du médecin.
                  </p>
                </div>
              </header>
              <div className="form-grid">
                <Field label="Médecin traitant">
                  <select
                    required
                    value={practitionerId}
                    onChange={(e) => {
                      setPractitionerId(e.target.value);
                      setAvailabilityId("");
                    }}
                  >
                    <option value="">Sélectionner un médecin…</option>
                    {(overview.practitioners || []).map((p) => (
                      <option value={p.id} key={p.id}>
                        {p.name} · {p.specialty}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Motif">
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  >
                    <option>Consultation classique</option>
                    <option>Visite de contrôle</option>
                    <option>Urgence</option>
                    <option>Vaccination</option>
                  </select>
                </Field>
                <Field label="Créneau libre">
                  <button
                    type="button"
                    className={`slot-picker-trigger ${selectedSlot ? "selected" : ""}`}
                    disabled={!practitionerId}
                    onClick={() => setSlotPickerOpen(true)}
                  >
                    <CalendarDays />
                    <span>
                      <strong>
                        {selectedSlot
                          ? new Date(selectedSlot.starts_at).toLocaleString(
                              "fr-FR",
                              { dateStyle: "long", timeStyle: "short" },
                            )
                          : practitionerId
                            ? slots.length
                              ? "Choisir un créneau dans l’agenda"
                              : "Aucun créneau disponible"
                            : "Sélectionnez d’abord un médecin"}
                      </strong>
                      <small>
                        {selectedSlot
                          ? `${selectedSlot.type} · Modifier le choix`
                          : slots.length
                            ? `${slots.length} créneau(x) futur(s) libre(s)`
                            : ""}
                      </small>
                    </span>
                    <ChevronRight />
                  </button>
                  {practitionerId && !slots.length && (
                    <small className="field-help warning">
                      Ce médecin ne possède aucun créneau futur libre. Ajoutez
                      d’abord une plage depuis le menu Agenda.
                    </small>
                  )}
                </Field>
                <Field label="Notes complémentaires">
                  <textarea
                    name="notes"
                    rows="3"
                    placeholder="Informations utiles pour la consultation…"
                  />
                </Field>
              </div>
            </section>
          </div>
          <aside className="appointment-summary">
            <span className="draft-badge">BROUILLON</span>
            <h3>Résumé</h3>
            <dl>
              <div>
                <dt>Patient</dt>
                <dd>
                  {patientMode === "existing"
                    ? selectedPatient?.name || "À sélectionner"
                    : "Nouveau patient"}
                </dd>
              </div>
              <div>
                <dt>Médecin</dt>
                <dd>{practitioner?.name || "Non sélectionné"}</dd>
              </div>
              <div>
                <dt>Motif</dt>
                <dd>{reason}</dd>
              </div>
              <div>
                <dt>Date et heure</dt>
                <dd>
                  {selectedSlot
                    ? new Date(selectedSlot.starts_at).toLocaleString("fr-FR", {
                        dateStyle: "long",
                        timeStyle: "short",
                      })
                    : "À définir"}
                </dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>
                  <span className="source-chip">ELIKYACARE-BACKOFFICE</span>
                </dd>
              </div>
            </dl>
            <div className="summary-actions">
              <button
                className="primary"
                disabled={
                  busy ||
                  !practitionerId ||
                  !availabilityId ||
                  (patientMode === "existing" && !patientId)
                }
              >
                <Check />
                {busy ? "Réservation…" : "Confirmer le rendez-vous"}
              </button>
              <button type="button" className="subtle" onClick={onClose}>
                Annuler
              </button>
            </div>
          </aside>
        </div>
        {slotPickerOpen && (
          <div
            className="slot-picker-overlay"
            onMouseDown={(e) =>
              e.target === e.currentTarget && setSlotPickerOpen(false)
            }
          >
            <div className="slot-picker-dialog">
              <header>
                <div>
                  <span className="modal-kicker">AGENDA EN TEMPS RÉEL</span>
                  <h3>Choisir un créneau</h3>
                  <p>
                    {practitioner?.name} · {practitioner?.specialty}
                  </p>
                </div>
                <IconButton onClick={() => setSlotPickerOpen(false)}>
                  <X />
                </IconButton>
              </header>
              <div className="slot-picker-legend">
                <span>
                  <i />
                  Créneaux disponibles
                </span>
                <b>{slots.length} disponibilité(s)</b>
              </div>
              <div className="slot-days">
                {Object.entries(slotsByDay).map(([day, daySlots]) => (
                  <section key={day}>
                    <div className="slot-day-label">
                      <CalendarDays />
                      <div>
                        <strong>
                          {new Date(`${day}T12:00:00`).toLocaleDateString(
                            "fr-FR",
                            { weekday: "long", day: "numeric", month: "long" },
                          )}
                        </strong>
                        <small>{daySlots.length} créneau(x)</small>
                      </div>
                    </div>
                    <div className="slot-time-grid">
                      {daySlots.map((slot) => (
                        <button
                          type="button"
                          className={
                            String(slot.id) === availabilityId ? "selected" : ""
                          }
                          key={slot.id}
                          onClick={() => setAvailabilityId(String(slot.id))}
                        >
                          <Clock3 />
                          <strong>
                            {new Date(slot.starts_at).toLocaleTimeString(
                              "fr-FR",
                              { hour: "2-digit", minute: "2-digit" },
                            )}
                          </strong>
                          <small>
                            {Math.round(
                              (new Date(slot.ends_at) -
                                new Date(slot.starts_at)) /
                                60000,
                            )}{" "}
                            min
                          </small>
                          {String(slot.id) === availabilityId && <Check />}
                        </button>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
              <footer>
                <div>
                  {selectedSlot ? (
                    <>
                      <Check />
                      <span>
                        <strong>Créneau sélectionné</strong>
                        {new Date(selectedSlot.starts_at).toLocaleString(
                          "fr-FR",
                          { dateStyle: "long", timeStyle: "short" },
                        )}
                      </span>
                    </>
                  ) : (
                    <span>Sélectionnez une heure pour continuer.</span>
                  )}
                </div>
                <button
                  type="button"
                  className="primary"
                  disabled={!availabilityId}
                  onClick={() => setSlotPickerOpen(false)}
                >
                  Confirmer ce créneau
                </button>
              </footer>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
function Modal({ page, overview, onClose }) {
  const isUser = page === "users";
  const isAdmin = overview.mode === "PLATFORM_ADMIN";
  const [facilities] = useState(
    overview.mode === "PLATFORM_ADMIN"
      ? overview.facilities || []
      : overview.available_facilities || [],
  );
  const [roles, setRoles] = useState([]);
  const platformRoles = ["Gestionnaire", "Secrétaire", "Médecin", "Auditeur", "Monitoring"];
  const [facilityId, setFacilityId] = useState(
    String(overview.facility?.id || overview.facilities?.[0]?.id || ""),
  );
  const [selectedFacilityIds, setSelectedFacilityIds] = useState(() => {
    const initial = overview.facility?.id || overview.facilities?.[0]?.id;
    return initial ? [String(initial)] : [];
  });
  const [error, setError] = useState("");
  const token = sessionStorage.getItem("kimyacare_access");
  useEffect(() => {
    if (isAdmin || !facilityId) return;
    setRoles([]);
    fetch(`${API_BASE}/platform/backoffice/facilities/${facilityId}/roles/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((items) => setRoles(Array.isArray(items) ? items : []))
      .catch(() => setRoles([]));
  }, [facilityId, token, isAdmin]);
  async function submit(e) {
    e.preventDefault();
    if (!isUser) {
      onClose();
      return;
    }
    const data = Object.fromEntries(new FormData(e.currentTarget));
    data.facility_ids = selectedFacilityIds;
    data.role_name = isAdmin
      ? data.role_name
      : roles.find((role) => String(role.id) === String(data.role_id))?.name;
    setError("");
    const response = await fetch(
      `${API_BASE}/platform/backoffice/facility-members/batch/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );
    if (response.ok) {
      onClose();
      return;
    }
    const body = await response.json();
    setError(body.detail || Object.values(body)[0] || "Création impossible.");
  }
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <form className="modal" onSubmit={submit}>
        <div className="modal-head">
          <div>
            <h2>
              {isUser
                ? isAdmin
                  ? "Créer ou rattacher un utilisateur"
                  : "Créer ou rattacher un utilisateur"
                : "Créer un nouvel élément"}
            </h2>
            <p>
              {isUser
                ? "Ajoutez un membre et définissez ses accès à la structure."
                : "Renseignez les informations nécessaires."}
            </p>
          </div>
          <IconButton onClick={onClose}>
            <X />
          </IconButton>
        </div>
        <div className="modal-body">
          {error && <div className="login-error">{String(error)}</div>}
          <div className="form-grid">
            <Field label="Prénom">
              <input name="first_name" placeholder="Ex. Marie" required />
            </Field>
            <Field label="Nom">
              <input name="last_name" placeholder="Ex. Kabongo" required />
            </Field>
            <Field label="Adresse e-mail">
              <input name="email" type="email" placeholder="nom@structure.cd" />
            </Field>
            <Field label="Téléphone">
              <input name="phone_number" placeholder="+243 …" required />
            </Field>
            <div className="field facility-multi-field">
              <span>Établissements</span>
              <div className="facility-check-list">
                {facilities.map((facility) => {
                  const checked = selectedFacilityIds.includes(
                    String(facility.id),
                  );
                  return (
                    <label
                      className={checked ? "selected" : ""}
                      key={facility.id}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked
                            ? selectedFacilityIds.filter(
                                (id) => id !== String(facility.id),
                              )
                            : [...selectedFacilityIds, String(facility.id)];
                          setSelectedFacilityIds(next);
                          if (next.length) setFacilityId(next[0]);
                        }}
                      />
                      <span>
                        <Building2 />
                        <b>{facility.name}</b>
                        <small>
                          {facility.role || facility.type || "Établissement"}
                        </small>
                      </span>
                      <i>{checked && <Check />}</i>
                    </label>
                  );
                })}
              </div>
              <small className="field-help">
                {selectedFacilityIds.length} établissement(s) sélectionné(s)
              </small>
            </div>
            <Field label="Rôle">
              {isAdmin ? (
                <select name="role_name" defaultValue="Gestionnaire" required>
                  {platformRoles.map((role) => <option value={role} key={role}>{role}</option>)}
                </select>
              ) : (
                <select name="role_id" required>
                  {roles.map((r) => (
                    <option value={r.id} key={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Mot de passe initial">
              <input
                name="password"
                type="password"
                minLength="8"
                required
                placeholder="8 caractères minimum"
              />
            </Field>
          </div>
          {isAdmin && <input type="hidden" name="activate" value="true" />}
          {isUser && (
            <div className="notice">
              <ShieldCheck />
              <p>
                <strong>Périmètre protégé</strong>
                <span>
                  {isAdmin
                    ? "Le gestionnaire sera activé dans tous les établissements sélectionnés."
                    : "L’utilisateur sera rattaché uniquement aux établissements sélectionnés parmi votre propre périmètre."}
                </span>
              </p>
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button type="button" className="subtle" onClick={onClose}>
            Annuler
          </button>
          <button
            className="primary"
            type="submit"
            disabled={
              isUser &&
              (!selectedFacilityIds.length || (!isAdmin && !roles.length))
            }
          >
            {isUser ? "Créer ou rattacher" : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(() =>
    Boolean(sessionStorage.getItem("kimyacare_access")),
  );
  const [page, setPage] = useState("dashboard");
  const [sideOpen, setSideOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("elikyacare_sidebar_collapsed") === "true",
  );
  const [modal, setModal] = useState(false);
  const [pageQuery, setPageQuery] = useState("");
  const [overview, setOverview] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedFacilityId, setSelectedFacilityId] = useState(
    () => sessionStorage.getItem("kimyacare_facility") || "",
  );
  useEffect(() => {
    const openSearch = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        document.querySelector(".global-search input")?.focus();
      }
    };
    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, []);
  useEffect(() => setPageQuery(""), [page]);
  useEffect(() => {
    if (!authenticated || page !== "monitoring") return undefined;
    const timer = window.setInterval(() => setRefreshKey((value) => value + 1), 3000);
    return () => window.clearInterval(timer);
  }, [authenticated, page]);
  useEffect(() => {
    if (!authenticated) return;
    const token = sessionStorage.getItem("kimyacare_access");
    const query = selectedFacilityId
      ? `?facility_id=${encodeURIComponent(selectedFacilityId)}`
      : "";
    fetch(`${API_BASE}/platform/backoffice/overview/${query}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.detail || "Accès refusé.");
        return body;
      })
      .then((body) => {
        setOverview(body);
        if (body.identity?.role === "Monitoring" && page === "dashboard") {
          setPage("monitoring");
        }
        if (body.facility?.id) {
          setSelectedFacilityId(String(body.facility.id));
          sessionStorage.setItem(
            "kimyacare_facility",
            String(body.facility.id),
          );
        }
      })
      .catch((error) => setLoadError(error.message));
  }, [authenticated, refreshKey, selectedFacilityId]);
  if (!authenticated)
    return <Login onAuthenticated={() => setAuthenticated(true)} />;
  if (loadError)
    return (
      <div className="state-page">
        <ShieldCheck />
        <h1>Accès au backoffice indisponible</h1>
        <p>{loadError}</p>
        <button
          className="primary"
          onClick={() => {
            sessionStorage.removeItem("kimyacare_access");
            location.reload();
          }}
        >
          Revenir à la connexion
        </button>
      </div>
    );
  if (!overview)
    return (
      <div className="state-page">
        <Activity />
        <h1>Chargement des données réelles…</h1>
      </div>
    );
  const isAdmin = overview.mode === "PLATFORM_ADMIN";
  const canCreateUser =
    isAdmin || (overview.permissions || []).includes("members.manage");
  const canCreateAppointment =
    !isAdmin && (overview.permissions || []).includes("appointments.manage");
  const normalizedQuery = pageQuery.trim().toLowerCase();
  const matchesQuery = (item) =>
    JSON.stringify(item).toLowerCase().includes(normalizedQuery);
  const pageOverview = normalizedQuery
    ? {
        ...overview,
        ...(page === "appointments" ? { appointments: (overview.appointments || []).filter(matchesQuery) } : {}),
        ...(page === "agenda" ? { availability: (overview.availability || []).filter(matchesQuery) } : {}),
        ...(page === "users" ? {
          users: (overview.users || []).filter(matchesQuery),
          members: (overview.members || []).filter(matchesQuery),
        } : {}),
        ...(page === "practitioners" ? {
          providers: (overview.providers || []).filter(matchesQuery),
          practitioners: (overview.practitioners || []).filter(matchesQuery),
        } : {}),
        ...(page === "facilities" ? { facilities: (overview.facilities || []).filter(matchesQuery) } : {}),
        ...(page === "validations" ? { validations: (overview.validations || []).filter(matchesQuery) } : {}),
        ...(page === "ambulances" ? { providers: (overview.providers || []).filter(matchesQuery) } : {}),
        ...(page === "audit" ? { audit: (overview.audit || []).filter(matchesQuery) } : {}),
      }
    : overview;
  return (
    <div className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <Sidebar
        page={page}
        setPage={setPage}
        open={sideOpen}
        setOpen={setSideOpen}
        mode={overview.mode}
        collapsed={sidebarCollapsed}
        setCollapsed={(value) => {
          setSidebarCollapsed(value);
          localStorage.setItem("elikyacare_sidebar_collapsed", String(value));
        }}
      />
      {sideOpen && (
        <div className="mobile-scrim" onClick={() => setSideOpen(false)} />
      )}
      <div className="main-shell">
        <Header
          setOpen={setSideOpen}
          overview={overview}
          query={pageQuery}
          setQuery={setPageQuery}
          onFacilityChange={(facilityId) => {
            sessionStorage.setItem("kimyacare_facility", facilityId);
            setSelectedFacilityId(facilityId);
            setOverview(null);
            setPage("dashboard");
          }}
          onLogout={() => {
            sessionStorage.removeItem("kimyacare_access");
            sessionStorage.removeItem("kimyacare_facility");
            setOverview(null);
            setAuthenticated(false);
            setPage("dashboard");
          }}
        />
        <main>
          <PageHeader
            page={page}
            onAdd={
              (page === "users" && canCreateUser) ||
              (page === "appointments" && canCreateAppointment) ||
              (page === "facilities" && isAdmin)
                ? () => setModal(true)
                : null
            }
          />
          {page === "dashboard" ? (
            <Dashboard setPage={setPage} overview={overview} />
          ) : page === "monitoring" ? (
            <MonitoringPage overview={overview} onRefresh={() => setRefreshKey((value) => value + 1)} />
          ) : page === "validations" ? (
            <ValidationsPage
              overview={pageOverview}
              onRefresh={() => setRefreshKey((value) => value + 1)}
            />
          ) : page === "appointments" ? (
            <Appointments
              overview={pageOverview}
              onRefresh={() => setRefreshKey((value) => value + 1)}
            />
          ) : page === "agenda" ? (
            <Agenda
              overview={pageOverview}
              onRefresh={() => setRefreshKey((value) => value + 1)}
            />
          ) : page === "users" ? (
            <UsersPage
              overview={pageOverview}
              onRefresh={() => setRefreshKey((value) => value + 1)}
            />
          ) : page === "facilities" && isAdmin ? (
            <FacilitiesPage
              overview={pageOverview}
              onRefresh={() => setRefreshKey((value) => value + 1)}
            />
          ) : page === "practitioners" && isAdmin ? (
            <ProvidersPage
              overview={pageOverview}
              onRefresh={() => setRefreshKey((value) => value + 1)}
            />
          ) : page === "practitioners" ? (
            <FacilityPractitionersPage
              overview={pageOverview}
              onRefresh={() => setRefreshKey((value) => value + 1)}
            />
          ) : page === "ambulances" && isAdmin ? (
            <AmbulancesPage
              overview={pageOverview}
              onRefresh={() => setRefreshKey((value) => value + 1)}
            />
          ) : page === "roles" ? (
            <Roles overview={overview} />
          ) : page === "settings" ? (
            <SettingsPage />
          ) : page === "notifications" && isAdmin ? (
            <CarouselPage
              overview={overview}
              onRefresh={() => setRefreshKey((value) => value + 1)}
            />
          ) : (
            <GenericTable page={page} overview={pageOverview} />
          )}
        </main>
        <footer>
          <span>© 2026 ElikyaCare · Administration sécurisée</span>
          <span>
            Politique de confidentialité · Support · Statut des services
          </span>
        </footer>
      </div>
      {modal &&
        (page === "appointments" ? (
          <AppointmentCreateModal
            overview={overview}
            onClose={() => setModal(false)}
            onSaved={() => {
              setModal(false);
              setRefreshKey((value) => value + 1);
            }}
          />
        ) : page === "facilities" && isAdmin ? (
          <FacilityCreateModal
            onClose={() => setModal(false)}
            onSaved={() => {
              setModal(false);
              setRefreshKey((value) => value + 1);
            }}
          />
        ) : (
          <Modal
            page={page}
            overview={overview}
            onClose={() => {
              setModal(false);
              setRefreshKey((value) => value + 1);
            }}
          />
        ))}
    </div>
  );
}
